import type { Namespace } from '../types';

// DE drafted — queued for native review. Site asset register (R-122): the CMDB
// at the site tier — full lifecycle, taxonomy classes, schema-driven attributes.
export const siteAssets = {
  // R-238 §6 — the prototype's "Recent scans this session". Session-only: it answers
  // "what have I already done on this walk", not "where has this unit been".
  recentScans: { de: 'Zuletzt gescannt (diese Sitzung)', en: 'Recent scans this session' },
  scanMatched: { de: 'gefunden', en: 'matched' },
  scanMissRow: { de: 'nicht gefunden', en: 'not found' },
  stockroomTitle: { de: 'Lager', en: 'Stockroom' },
  stockroomDesc: {
    de: 'Assets auf Lager — bereit für Einsatz oder Tausch (Garantie-first-Auswahl).',
    en: 'Assets in stock — ready to deploy or swap (warranty-first pick).',
  },
  title: { de: 'Asset-Register', en: 'Asset Register' },
  desc: {
    de: 'Alle Assets dieses Standorts — Lebenszyklus, Standort, Klassen-Attribute und Finanzdaten. Betrieb lokal, Spiegel in die Cloud.',
    en: 'Every asset at this site — lifecycle, location, class attributes and financials. Operated locally, mirrored to the cloud.',
  },

  statTotal: { de: 'Assets', en: 'Assets' },
  statStock: { de: 'Im Lager', en: 'In stock' },
  statDeployed: { de: 'Im Einsatz', en: 'Deployed' },
  statFaulty: { de: 'Gestört', en: 'Faulty' },

  colTag: { de: 'Tag', en: 'Tag' },
  colName: { de: 'Name', en: 'Name' },
  colClass: { de: 'Klasse', en: 'Class' },
  colLocation: { de: 'Standort', en: 'Location' },
  colState: { de: 'Lebenszyklus', en: 'Lifecycle' },
  colOpStatus: { de: 'Status', en: 'Status' },
  colCriticality: { de: 'Kritikalität', en: 'Criticality' },
  colWarranty: { de: 'Garantie bis', en: 'Warranty until' },
  colCustodian: { de: 'Verantwortlich', en: 'Custodian' },
  colCost: { de: 'Kosten', en: 'Cost' },

  // R-235: the prototype's eight states, its wording.
  stateInStock: { de: 'Auf Lager', en: 'In stock' },
  stateReserved: { de: 'Reserviert', en: 'Reserved' },
  stateInTransit: { de: 'Unterwegs', en: 'In transit' },
  stateInUse: { de: 'Im Einsatz', en: 'In use' },
  stateInMaintenance: { de: 'In Wartung', en: 'In maintenance' },
  stateRetired: { de: 'Stillgelegt', en: 'Retired' },
  stateDisposed: { de: 'Entsorgt', en: 'Disposed' },
  stateMissing: { de: 'Vermisst', en: 'Missing' },

  // R-235: why a unit moved. The backend stores stable keys; this is where the
  // prototype's exact wording lives, which is the point of not storing English.
  reasonRegistered: { de: 'Registriert', en: 'Registered' },
  reasonIssuedForInstall: { de: 'Zur Montage ausgegeben', en: 'Issued for install' },
  reasonInstallation: { de: 'Montage', en: 'Installation' },
  reasonIssuedToEndUser: { de: 'An Nutzer ausgegeben', en: 'Issued to end user' },
  reasonTransferBetweenLocations: { de: 'Standortwechsel', en: 'Transfer between locations' },
  reasonReturnedToStore: { de: 'Ans Lager zurück', en: 'Returned to store' },
  reasonFaultSentToMaintenance: { de: 'Störung — zur Wartung', en: 'Fault — sent to maintenance' },
  reasonRepairedAndReturned: { de: 'Repariert und zurück', en: 'Repaired and returned' },
  reasonReAssigned: { de: 'Neu zugewiesen', en: 'Re-assigned' },
  reasonEndOfLife: { de: 'Lebensende', en: 'End of life' },
  reasonBeyondEconomicRepair: { de: 'Reparatur unwirtschaftlich', en: 'Beyond economic repair' },
  reasonDisposalConfirmed: { de: 'Entsorgung bestätigt', en: 'Disposal confirmed' },
  reasonStockCountDiscrepancy: { de: 'Inventurdifferenz', en: 'Stock count discrepancy' },
  reasonFound: { de: 'Gefunden', en: 'Found' },
  reasonReTagged: { de: 'Neu etikettiert', en: 'Re-tagged' },
  reasonWrittenOff: { de: 'Abgeschrieben', en: 'Written off' },
  reasonReservedForChange: { de: 'Für Change reserviert', en: 'Reserved for change' },
  reasonReservationReleased: { de: 'Reservierung aufgehoben', en: 'Reservation released' },
  reasonReplacedRemovedFromService: { de: 'Ersetzt — außer Betrieb', en: 'Replaced — removed from service' },

  opOperational: { de: 'Betriebsbereit', en: 'Operational' },
  opStandby: { de: 'Standby', en: 'Standby' },
  opOutOfService: { de: 'Außer Betrieb', en: 'Out of service' },
  opFaulty: { de: 'Gestört', en: 'Faulty' },
  opOffline: { de: 'Offline', en: 'Offline' },

  critCritical: { de: 'Kritisch', en: 'Critical' },
  critHigh: { de: 'Hoch', en: 'High' },
  critMedium: { de: 'Mittel', en: 'Medium' },
  critLow: { de: 'Niedrig', en: 'Low' },

  filterAllStates: { de: 'Alle Zustände', en: 'All states' },
  filterAllClasses: { de: 'Alle Klassen', en: 'All classes' },
  filterAllCrit: { de: 'Alle Kritikalitäten', en: 'All criticalities' },
  filterAllOp: { de: 'Alle Betriebsstatus', en: 'All op-statuses' },
  searchPlaceholder: { de: 'Tag, Name, Seriennr., Modell…', en: 'Tag, name, serial, model…' },

  newAsset: { de: 'Neues Asset', en: 'New asset' },
  importCsv: { de: 'CSV importieren', en: 'Import CSV' },
  importTitle: { de: 'Assets aus CSV importieren', en: 'Import assets from CSV' },
  importHint: {
    de: 'CSV mit Kopfzeile hochladen. Pflichtspalten: class_code, name. Jede Zeile wird gegen die Klassen-Taxonomie, Attribut-Definitionen und Location geprüft (Prüflauf zuerst).',
    en: 'Upload a CSV with a header row. Required columns: class_code, name. Every row is validated against the class taxonomy, attribute defs and location (dry-run first).',
  },
  importApply: { de: 'Import anwenden', en: 'Apply import' },
  importDryRun: {
    de: 'Prüflauf — {applied} von {total} Zeilen gültig.',
    en: 'Dry run — {applied} of {total} rows valid.',
  },
  importApplied: {
    de: '{applied} von {total} Zeilen importiert.',
    en: 'Imported {applied} of {total} rows.',
  },
  importRowError: { de: 'Zeile {line}: {error}', en: 'Line {line}: {error}' },
  // D-149: no longer necessarily a sub-type — any LEAF class can carry models,
  // assets and labels, so the parenthetical was a promise the form does not keep.
  fieldClass: { de: 'Asset-Klasse', en: 'Asset class' },
  fieldName: { de: 'Bezeichnung', en: 'Name' },
  fieldSerial: { de: 'Seriennummer', en: 'Serial number' },
  serialDupWarn: {
    de: 'Seriennummer existiert bereits auf {tag} — prüfen, ob dies wirklich ein neues Gerät ist.',
    en: 'Serial already exists on {tag} — check this is really a new unit.',
  },
  fieldManufacturer: { de: 'Hersteller', en: 'Manufacturer' },
  fieldModel: { de: 'Modell', en: 'Model' },
  fieldSupplier: { de: 'Lieferant', en: 'Supplier' },
  // Shown beside a supplier suggestion that procurement has not cleared.
  // It is offered anyway: a unit may have been bought from them.
  supplierUnapproved: { de: 'nicht freigegeben', en: 'not approved' },
  // R-168: the vendor name is recorded but matches no row in the supplier
  // master, so this asset does not appear under that supplier and a rename
  // there will not reach it.
  supplierUnlinked: { de: 'nicht mit Lieferantenstamm verknüpft', en: 'not linked to the supplier master' },
  // The delivery note said something else — kept, because it is the received
  // document, while the master name above is the current one.
  supplierReceivedAs: { de: 'geliefert als „{name}“', en: 'received as “{name}”' },
  fieldPoRef: { de: 'Bestell-Nr. (PO/PR)', en: 'PO/PR reference' },
  fieldLocation: { de: 'Standort', en: 'Location' },
  fieldManagedByGroup: { de: 'Zuständige Gruppe (Routing)', en: 'Managed by group (routing)' },
  fieldWarranty: { de: 'Garantie bis', en: 'Warranty until' },
  fieldPurchaseDate: { de: 'Kaufdatum', en: 'Purchase date' },
  fieldCost: { de: 'Anschaffungskosten (AED)', en: 'Acquisition cost (AED)' },
  fieldCriticality: { de: 'Kritikalität', en: 'Criticality' },
  fieldInitialState: { de: 'Anfangszustand', en: 'Initial state' },
  fieldNotes: { de: 'Bemerkungen', en: 'Notes' },
  attributesSection: { de: 'Klassen-Attribute', en: 'Class attributes' },
  selectLocation: { de: '— Standort wählen —', en: '— select location —' },
  // R-165 / D-120 — the product-catalogue picker on the create form.
  // The catalogue entry an asset is an instance of. Called "Model" rather than
  // "SKU" on purpose: SKU is already taken twice here — the ecommerce produce
  // catalogue and the ERP warehouse item code — and a third meaning next to
  // spares would be the worst kind of collision.
  fieldModelCatalogue: { de: 'Modell', en: 'Model' },
  selectModel: { de: '— Modell wählen —', en: '— pick a model —' },
  fieldUsefulLife: { de: 'Nutzungsdauer (Jahre)', en: 'Useful life (years)' },
  modelNew: { de: '+ Neues Modell', en: '+ New model' },
  modelNewTitle: { de: 'Neues Modell anlegen', en: 'Add a new model' },
  modelNewHint: {
    de: 'Sofort verwendbar. Wird mit der Cloud abgeglichen, sobald die Verbindung steht — Duplikate werden dort zusammengeführt.',
    en: 'Usable immediately. It syncs to the cloud when the link is up, where duplicates are merged.',
  },
  modelDerived: {
    de: 'Klasse {class} — Hersteller, Modell, Nutzungsdauer und Vorgaben kommen aus dem Modell.',
    en: 'Class {class} — manufacturer, model, useful life and the defaults all come from the model.',
  },
  modelProvisional: { de: 'lokal, noch nicht in der Cloud', en: 'local, not yet in the cloud' },
  // R-166 — say which attributes the product supplied, and that they are editable.
  attrsFromModel: {
    de: '{count} Merkmal(e) aus {model} übernommen. Zum Überschreiben einfach ändern.',
    en: '{count} attribute(s) filled from {model}. Edit any of them to override.',
  },
  // D-148 — the sticker carries its class path inside the tag, so it can only go on
  // a unit of that class. Said here rather than let the server refuse the save.
  wizTagClassMismatch: {
    de: '⚠ Das Etikett {tag} wurde für {labelClass} gedruckt, dieses Gerät ist {class}. Bitte ein Etikett aus dem Pool dieser Klasse wählen oder ein Modell der Etikettenklasse.',
    en: "⚠ Tag {tag} was printed for {labelClass}, but this unit is {class}. Pick a tag from that class's pool, or a model of the tag's class.",
  },
  // D-146 — the prototype's `reqMissing`: a required PER-UNIT attribute blocks the
  // register, and the operator is told which one rather than left with a dead button.
  attrsRequiredMissing: {
    de: 'Von der Klasse verlangt: {attrs}',
    en: 'Required by the class: {attrs}',
  },
  modelPickHint: {
    de: 'Modell wählen — alles Produktbezogene wird übernommen. Nur die Seriennummer wird pro Einheit eingegeben.',
    en: 'Pick the model — everything about the product is filled in. Only the serial is typed per unit.',
  },
  // R-245 — the picker is scoped to the tag's class, so an empty list is a real
  // state and not a fault. Both ways out are named: the operator either takes a
  // different sticker, or authors the product they are holding.
  modelNoneForClass: { de: '— kein Modell für diese Klasse —', en: '— no model for this class —' },
  wizNoModelForTagClass: {
    de: '⚠ Für die Klasse {class} des Etiketts {tag} ist noch kein Modell katalogisiert. Mit „+ Neues Modell“ das Produkt anlegen — die Klasse ist dabei bereits gesetzt — oder in Schritt 2 ein Etikett einer anderen Klasse wählen.',
    en: '⚠ Nothing is catalogued yet for {class}, the class tag {tag} was printed for. Use “+ New model” to author the product you are holding — the class is already set — or go back to step 2 and pick a tag from another class.',
  },
  loading: { de: 'Lädt …', en: 'Loading …' },
  // R-165 P4 — receive-by-quantity: one serial per line.
  fieldSerialList: {
    de: 'Mehrere Einheiten (eine Seriennummer pro Zeile)',
    en: 'Multiple units (one serial per line)',
  },
  serialListPlaceholder: { de: 'SN-0001\nSN-0002\nSN-0003', en: 'SN-0001\nSN-0002\nSN-0003' },
  serialListHint: {
    de: 'Leer lassen, um eine einzelne Einheit anzulegen.',
    en: 'Leave empty to register a single unit.',
  },
  batchCount: { de: '{n} Einheiten werden angelegt', en: '{n} units will be registered' },
  selectClass: { de: '— Klasse wählen —', en: '— select class —' },
  create: { de: 'Anlegen', en: 'Create' },
  save: { de: 'Speichern', en: 'Save' },
  yes: { de: 'Ja', en: 'Yes' },
  no: { de: 'Nein', en: 'No' },
  cancel: { de: 'Abbrechen', en: 'Cancel' },
  edit: { de: 'Bearbeiten', en: 'Edit' },
  fieldDescription: { de: 'Beschreibung', en: 'Description' },
  nextPick: { de: 'Nächste Entnahme', en: 'Next pick' },
  removeDoc: { de: 'Dokument entfernen', en: 'Remove document' },
  underChange: { de: 'Geplanter Change aktiv · {number}', en: 'Under planned change · {number}' },
  capNotice: {
    de: 'Erste 500 Einträge angezeigt — Suche/Filter verfeinern.',
    en: 'First 500 rows shown — refine search/filters.',
  },
  docsMissing: {
    de: 'Pflichtdokumente fehlen: {kinds} (Workshop-Folie 12 — Foto ist beim Deploy Pflicht, Rest empfohlen).',
    en: 'Mandatory acquisition docs missing: {kinds} (workshop slide 12 — photo is hard-required at deploy, the rest recommended).',
  },
  docKind_photo: { de: 'Foto', en: 'photo' },
  docKind_po: { de: 'PO/PR', en: 'PO/PR' },
  docKind_datasheet: { de: 'Datenblatt', en: 'datasheet' },
  raiseEventPh: { de: 'PM-Aufgabe auslösen…', en: 'Raise PM task…' },
  eventRaised: { de: '{number} angelegt', en: '{number} raised' },
  addRelation: { de: 'Beziehung hinzufügen', en: 'Add relation' },
  // Accessible names for two pickers that had none: both sit bare in a toolbar
  // row with no <Field>, so until now they were unlabelled controls.
  relKind: { de: 'Beziehungsart', en: 'Relation kind' },
  docKind: { de: 'Dokumentart', en: 'Document kind' },
  removeRelation: { de: 'Beziehung entfernen', en: 'Remove relation' },
  selectAsset: { de: 'Asset wählen…', en: 'Select asset…' },
  statusFlaps: {
    de: 'Status wechselte {count}× ({from} – {to})',
    en: 'Status changed {count}× ({from} – {to})',
  },
  fieldYear: { de: 'Baujahr', en: 'Year of manufacture' },
  maintenanceSection: { de: 'Wartung', en: 'Maintenance' },
  maintenanceStrategy: { de: 'Wartungsstrategie', en: 'Maintenance strategy' },
  serviceContract: { de: 'Servicevertrag', en: 'Service contract' },

  recordTitle: { de: 'Asset-Datensatz', en: 'Asset record' },
  general: { de: 'Allgemein', en: 'General' },
  financial: { de: 'Finanzen', en: 'Financial' },
  history: { de: 'Historie', en: 'History' },
  relations: { de: 'Verknüpfte Assets', en: 'Related assets' },
  documents: { de: 'Dokumente', en: 'Documents' },
  ifrsCategory: { de: 'IFRS-Kategorie', en: 'IFRS category' },
  usefulLife: { de: 'Nutzungsdauer (Jahre)', en: 'Useful life (years)' },
  depreciation: { de: 'Abschreibungsmethode', en: 'Depreciation method' },
  costCenter: { de: 'Kostenstelle', en: 'Cost centre' },
  installDate: { de: 'Installationsdatum', en: 'Install date' },
  commissioningDate: { de: 'Inbetriebnahme', en: 'Commissioning date' },
  assignedTo: { de: 'Zugewiesen an', en: 'Assigned to' },
  mcRef: { de: 'Controller-Referenz', en: 'Controller ref' },
  lastServiced: { de: 'Letzte Wartung', en: 'Last serviced' },
  nextDue: { de: 'Nächste Wartung', en: 'Next due' },
  transitionTo: { de: 'Weiter zu', en: 'Move to' },
  setOpStatus: { de: 'Status setzen', en: 'Set status' },
  changeStatus: { de: 'Status ändern', en: 'Change status' },
  changeStatusTitle: { de: 'Betriebsstatus ändern — {tag}', en: 'Change operational status — {tag}' },
  changeStatusHint: {
    de: 'Wählen Sie den neuen Betriebsstatus und speichern Sie, um ihn anzuwenden.',
    en: 'Pick the new operational status and Save to apply it.',
  },
  opStatusLabel: { de: 'Betriebsstatus', en: 'Operational status' },
  changeStatusSave: { de: 'Auf „{status}“ setzen', en: 'Set to “{status}”' },
  raisePm: { de: 'PM-Aufgabe auslösen', en: 'Raise PM task' },
  raisePmTitle: { de: 'PM-Aufgabe auslösen — {tag}', en: 'Raise PM task — {tag}' },
  raisePmHint: {
    de: 'Wählen Sie eine Wartungsvorlage und speichern Sie, um die Aufgabe anzulegen.',
    en: 'Pick a maintenance template and Save to create the task.',
  },
  raisePmLabel: { de: 'Wartungsvorlage', en: 'Maintenance template' },
  raisePmConfirm: { de: 'PM-Aufgabe anlegen', en: 'Raise PM task' },
  notFound: { de: 'Asset nicht gefunden', en: 'Asset not found' },
  emptyTitle: { de: 'Noch keine Assets', en: 'No assets yet' },
  emptyMsg: {
    de: 'Erfassen Sie das erste Asset über den Wareneingang.',
    en: 'Register the first asset through goods receipt.',
  },
  loadError: { de: 'Assets konnten nicht geladen werden', en: 'Failed to load assets' },
  open: { de: 'Öffnen', en: 'Open' },
  back: { de: 'Zurück', en: 'Back' },
  uploadDoc: { de: 'Dokument hochladen', en: 'Upload document' },
  createFailed: { de: 'Anlegen fehlgeschlagen', en: 'Create failed' },

  // R-129: QR labels + scanner.
  labelsTitle: { de: 'QR-Labels', en: 'QR labels' },
  labelsDesc: {
    de: '{count} Labels — Payload v1, Druck über den Browser.',
    en: '{count} labels — payload v1, print via the browser.',
  },
  labelsPrint: { de: 'Drucken', en: 'Print' },
  labelsSearch: { de: 'Label suchen (Tag / Name / Standort)', en: 'Search labels (tag / name / location)' },
  labelsSelectAll: { de: 'Alle auswählen', en: 'Select all' },
  labelsPrintSelected: { de: 'Auswahl drucken ({count})', en: 'Print selected ({count})' },
  labelsPrintThis: { de: 'Dieses Label drucken', en: 'Print this label' },
  labelsZoom: { de: 'QR vergrößern', en: 'Enlarge QR' },
  labelsEmpty: { de: 'Keine Labels', en: 'No labels' },
  labelsEmptyMsg: { de: 'Keine passenden Assets für diese Tags.', en: 'No assets match these tags.' },
  printLabel: { de: 'Label drucken', en: 'Print label' },
  qrLabel: { de: 'QR-Label', en: 'QR label' },
  scanTitle: { de: 'Scannen', en: 'Scan' },
  scanDesc: {
    de: 'Asset-QR scannen (Kamera) oder Tag manuell eingeben — öffnet Datensatz und offene Work Orders.',
    en: 'Scan an asset QR (camera) or type the tag — opens the record and its open work orders.',
  },
  scanNoCamera: {
    de: 'Kamera-Scan nicht verfügbar (Browser ohne BarcodeDetector) — Tag manuell eingeben.',
    en: 'Camera scan unavailable (browser lacks BarcodeDetector) — type the tag instead.',
  },
  scanManual: { de: 'Asset-Tag', en: 'Asset tag' },
  scanLookup: { de: 'Suchen', en: 'Look up' },
  scanNotFound: { de: 'Kein Asset mit Tag {tag}', en: 'No asset with tag {tag}' },
  scanToastFound: { de: 'Tag {tag} erfolgreich gescannt.', en: 'Tag {tag} scanned successfully.' },
  scanCameraPaused: {
    de: 'Kamera pausiert — Tag unten eingeben.',
    en: 'Camera paused — type the tag below.',
  },
  scanEmptyTitle: { de: 'Noch kein Tag gescannt', en: 'No tag scanned yet' },
  scanEmptyBody: {
    de: 'Kamera auf ein gedrucktes Xantory-Tag richten, oder links eingeben — ein Treffer erscheint hier.',
    en: 'Point the camera at a printed Xantory tag, or type it on the left — a match appears here.',
  },
  scanInvalid: {
    de: 'Kein gültiges Tag oder QR-Payload — erwartet z. B. VFU01-D30-0001.',
    en: 'Not a valid tag or QR payload — expected e.g. VFU01-D30-0001.',
  },
  scanAgain: { de: 'Neu scannen', en: 'Scan again' },
  scanOpenRecord: { de: 'Datensatz öffnen', en: 'Open record' },
  scanOpenWos: { de: 'Offene Work Orders ({count})', en: 'Open work orders ({count})' },
  scanStartWo: { de: 'Starten', en: 'Start' },
  scanCompleteWo: { de: 'Per Scan abschließen', en: 'Complete via scan' },
  scanCompleted: { de: 'Abgeschlossen (qr_scan)', en: 'Completed (qr_scan)' },

  // R-130: performance KPIs.
  performance: { de: 'Performance', en: 'Performance' },
  kpiUptime: { de: 'Verfügbarkeit (90 T)', en: 'Uptime (90d)' },
  kpiMtbf: { de: 'MTBF', en: 'MTBF' },
  kpiMttr: { de: 'MTTR', en: 'MTTR' },
  kpiIncidents: { de: 'Incidents (Quartal)', en: 'Incidents (quarter)' },
  kpiClassAvg: { de: 'Klassen-Ø', en: 'class avg' },
  kpiRank: { de: 'Rang in Klasse', en: 'Rank in class' },
  kpiRankValue: { de: '{rank}. von {total} (Zuverlässigkeit)', en: '{rank} of {total} (reliability)' },
  kpiSample: {
    de: 'Ø über {n} Assets der Klasse (flottenweit, nächtlich) · Stand: {asOf}',
    en: 'avg over {n} class assets (fleet-wide, nightly) · as of {asOf}',
  },
  terminalTransitionTitle: { de: 'Asset {tag} → {state}?', en: 'Move asset {tag} to {state}?' },
  terminalTransitionMsg: {
    de: 'Dieser Schritt beendet den Lebenszyklus und ist nicht umkehrbar. Tag eingeben, um zu bestätigen.',
    en: 'This step ends the lifecycle and cannot be undone. Type the tag to confirm.',
  },
  removeDocTitle: { de: 'Dokument „{name}“ entfernen?', en: 'Remove document “{name}”?' },
  removeDocMsg: {
    de: 'Das Dokument wird aus der Akte entfernt (Soft-Delete, im Ereignisprotokoll nachvollziehbar).',
    en: 'The document is removed from the record (soft delete, traceable in the event log).',
  },

  // R-235: the All Assets list, reshaped to the prototype's columns and tiles.
  colSerial: { de: 'Seriennr.', en: 'Serial' },
  colModel: { de: 'Modell (SKU)', en: 'Model (SKU)' },
  colPlacement: { de: 'Lager / Standort', en: 'Warehouse / Location' },
  colAssignedTo: { de: 'Zugewiesen an', en: 'Assigned to' },
  colManagedBy: { de: 'Verantwortlich', en: 'Managed by' },
  colUpdated: { de: 'Geändert', en: 'Updated' },
  colActions: { de: 'Aktionen', en: 'Actions' },
  serialNotAvailable: { de: 'nicht vorhanden', en: 'not available' },
  reservedNote: {
    de: 'für einen genehmigten Change reserviert',
    en: 'held for an approved change',
  },
  statUnregistered: { de: 'Unregistrierte Geräte', en: 'Unregistered devices' },
  registerAsset: { de: '+ Asset registrieren', en: '+ Register asset' },
  exportCsv: { de: 'CSV exportieren', en: 'Export CSV' },
  moveAction: { de: 'Bewegen', en: 'Move' },
  clearFilters: { de: 'Filter zurücksetzen', en: 'Clear' },
  filterAllDomains: { de: 'Alle Domänen', en: 'Domain: all' },
  filterAllLocations: { de: 'Alle Standorte', en: 'Location: all' },
  domainOffice: { de: 'Büro', en: 'Office' },
  domainFarm: { de: 'Farm', en: 'Farm' },

  // R-235: the 3-step intake wizard.
  wizTitle: { de: 'Registrieren / Wareneingang', en: 'Register / Intake' },
  wizDesc: {
    de: 'Zwei Scans verbinden die Seriennummer des Herstellers mit einem vorgedruckten Xantory-Tag; danach liegt das Gerät als „Auf Lager“ im Lagerort.',
    en: 'Two scans marry the manufacturer’s serial to a pre-printed Xantory tag, then the unit lands in a store as In stock.',
  },
  wizStep1: { de: 'Seriennummer scannen', en: 'Scan device serial' },
  wizStep1Desc: { de: 'Barcode am Gerät oder Karton', en: 'Barcode on device or box' },
  wizStep2: { de: 'Xantory-QR-Tag scannen', en: 'Scan Xantory QR tag' },
  wizStep2Desc: { de: 'Aus dem vorgedruckten Pool', en: 'From the pre-printed pool' },
  wizStep3: { de: 'Prüfen & registrieren', en: 'Confirm & register' },
  wizStep3Desc: { de: 'Klasse, Modell, Lagerort', en: 'Class, model, store' },
  wizStep1Hint: {
    de: 'Die Seriennummer wird gegen das Register geprüft, bevor es weitergeht — ein Duplikat wird hier gestoppt, nicht erst beim Speichern.',
    en: 'The serial is checked against the register before you continue — a duplicate is stopped here, not on save.',
  },
  wizSerialHint: {
    de: 'Scannen oder tippen. Ein Barcodeleser drückt Enter — das genügt.',
    en: 'Scan or type. A barcode reader presses Enter, which is enough on its own.',
  },
  wizSerialPh: { de: 'Seriennummer scannen oder tippen', en: 'Scan or type serial' },
  wizSerialDup: {
    de: 'Diese Seriennummer ist bereits als {tag} registriert — Duplikat blockiert.',
    en: 'Serial already registered as {tag} — duplicate blocked.',
  },
  wizNoSerial: { de: 'Keine Seriennummer vorhanden', en: 'Serial not available' },
  wizNoSerialHint: {
    de: 'Das Asset wird trotzdem angelegt — das TAG wird die Identität, gekennzeichnet zur Nachverfolgung. Häufig bei günstigen Sensoren und Farm-Armaturen: der Wareneingang darf daran nie scheitern.',
    en: 'The asset is still created — the TAG becomes the identity, flagged for follow-up. Common for cheap sensors and farm fittings: intake must never be blocked at the dock over an unreadable sticker.',
  },
  wizNoSerialReasonLabel: { de: 'Grund', en: 'Reason' },
  wizPickReason: { de: 'Grund wählen…', en: 'Pick a reason…' },
  wizContinueNoSerial: { de: 'Ohne Seriennummer weiter', en: 'Continue without a serial' },
  wizNoSerialReason: { de: 'keine Seriennummer · {reason}', en: 'no serial · {reason}' },
  serialAbsent_no_serial_printed: {
    de: 'Keine Seriennummer aufgedruckt',
    en: 'No serial printed on unit',
  },
  serialAbsent_unreadable: { de: 'Seriennummer unlesbar / beschädigt', en: 'Serial unreadable / damaged' },
  serialAbsent_sealed_unit: {
    de: 'Verschlossene Einheit — Seriennummer nicht zugänglich',
    en: 'Sealed unit — serial not accessible',
  },
  wizStep2Hint: {
    de: '{n} freie Labels im Pool. Ein Label abziehen, aufkleben, scannen — das verbindet Tag und Seriennummer.',
    en: '{n} unused labels in the pool. Peel one, stick it on, scan it — that marries the tag to the serial.',
  },
  wizTag: { de: 'Xantory-Tag', en: 'Xantory tag' },
  // R-245 — this used to promise the class was "still changeable" in step 3. It
  // never was (the class is derived from the model, there is no picker), and now
  // the model list itself is scoped to the tag's class, so the tag decides. Say
  // what is true: change the tag to change the class.
  wizTagHint: {
    de: 'Die Klasse des Labels bestimmt die Klasse der Einheit — in Schritt 3 werden nur Modelle dieser Klasse angeboten. Für eine andere Klasse hier ein anderes Etikett wählen.',
    en: 'The label’s class decides the unit’s class — step 3 only offers models of that class. To register another class, pick a different tag here.',
  },
  wizPickTag: { de: 'Tag aus dem Pool wählen…', en: 'Pick a tag from the pool…' },
  wizGenerateLabels: { de: 'Labels erzeugen', en: 'Generate labels' },
  wizScanned: { de: 'Gescannt', en: 'Scanned' },
  wizStockroom: { de: 'Lagerort', en: 'Store' },
  wizStockroomHint: {
    de: 'Wohin es physisch kommt. Der Zustand wird „Auf Lager“ — deshalb sind nur Lagerorte wählbar.',
    en: 'Where it physically lands now. The state becomes In stock, which is why only stores are listed.',
  },
  wizPickStockroom: { de: 'Lagerort wählen…', en: 'Pick the store…' },
  wizRegister: { de: 'Asset registrieren', en: 'Register asset' },
  wizRegisterNext: { de: 'Registrieren & nächstes scannen', en: 'Register & scan next' },
  wizRegistered: { de: '{tag} registriert', en: 'Registered {tag}' },
  wizContinue: { de: 'Weiter', en: 'Continue' },
  wizBack: { de: 'Zurück', en: 'Back' },
  wizStartOver: { de: 'Neu beginnen', en: 'Start over' },
  // Unregistered — the bind/reject/reopen writes report their outcome.
  toastDeviceBound: { de: 'Gerät erfolgreich verknüpft', en: 'Device bound successfully' },
  toastDeviceRejected: { de: 'Gerät abgewiesen', en: 'Device rejected' },
  toastDeviceReopened: { de: 'Gerät zurück auf offen gesetzt', en: 'Device moved back to pending' },
  backToList: { de: 'Zur Übersicht', en: 'Back to list' },
  wizNotReady: { de: 'Noch nicht bereit für den Wareneingang', en: 'Not ready for intake yet' },
  wizNotReadyMsg: {
    de: 'Der Wareneingang braucht {what}, bevor etwas angelegt werden kann.',
    en: 'Intake needs {what} before it can create anything.',
  },
  wizNeedClass: { de: 'eine serialisierte Asset-Klasse', en: 'a serialized asset class' },
  wizNeedModel: { de: 'mindestens ein Modell', en: 'at least one model' },
  wizNeedStockroom: { de: 'einen Lagerort', en: 'a store to receive into' },
  wizNeedLabel: { de: 'ein freies Label im Pool', en: 'an unused label in the pool' },
  // ── Register / Intake redesign ("★ A · Scan bench", 2026-10-07) ──────────────
  // Gate: the four prerequisites as a checklist, each with the page that fixes it.
  gateLede: {
    de: 'Der Wareneingang braucht alle vier, bevor etwas angelegt werden kann.',
    en: 'Intake needs all four before it can create anything.',
  },
  gateClasses: { de: 'Eine Asset-Klasse', en: 'An asset class' },
  gateModels: { de: 'Mindestens ein Modell (SKU)', en: 'At least one model (SKU)' },
  gateStore: { de: 'Ein Lagerort für den Eingang', en: 'A store to receive into' },
  gateLabels: { de: 'Ein freies QR-Label', en: 'An unused QR label' },
  gateFound: { de: '{n} vorhanden', en: '{n} found' },
  gateMissing: { de: 'Fehlt', en: 'Missing' },
  gateLoadLede: {
    de: 'Klassen, Modelle oder der Label-Pool wurden nicht geladen — es ist also nicht bekannt, dass etwas fehlt. Bitte nichts neu anlegen.',
    en: 'Asset classes, models or the label pool didn’t load, so nothing here is known to be missing — don’t go and create them.',
  },
  navLocations: { de: 'Standorte', en: 'Locations' },
  // Stepper + step bodies
  wizStep1Heading: { de: 'Herstellerseriennummer erfassen', en: 'Capture the manufacturer serial' },
  wizStep1Lede: {
    de: 'Kamera auf den Barcode am Gerät oder Karton richten oder einen Handscanner nutzen. Die Seriennummer wird vor dem Weiter gegen das Register geprüft — ein Duplikat wird hier gestoppt, nicht beim Speichern.',
    en: 'Point the camera at the barcode on the device or its box, or use a handheld reader. The serial is checked against the register before you continue — a duplicate is stopped here, not on save.',
  },
  wizSerialClear: {
    de: 'Nicht im Register — weiter möglich.',
    en: 'Not on the register — clear to continue.',
  },
  wizSerialDupLong: {
    de: 'Bereits registriert als {tag} — Duplikat blockiert. Fast immer ein erneuter Scan einer schon erfassten Einheit.',
    en: 'Already registered as {tag} — duplicate blocked. This is almost always a re-scan of a unit already booked in.',
  },
  wizOpenIt: { de: 'Öffnen', en: 'Open it' },
  wizRetryCheck: { de: 'Erneut prüfen', en: 'Retry check' },
  wizNoSerialTitle: { de: 'Keine lesbare Seriennummer?', en: 'No readable serial?' },
  wizNoSerialCta: { de: 'Seriennummer nicht vorhanden…', en: 'Serial not available…' },
  wizNoSerialModalHint: {
    de: 'Das Asset wird trotzdem angelegt — das Tag wird zur Identität und der Eintrag zur Nachverfolgung markiert. Den Grund angeben, damit er gezählt werden kann.',
    en: 'The unit is still registered — its Xantory tag becomes the identity and the record is flagged for follow-up. Say why, so it can be counted.',
  },
  wizTypeInstead: { de: 'Stattdessen tippen', en: 'Type instead' },
  wizUseCameraInstead: { de: 'Kamera stattdessen nutzen', en: 'Use camera instead' },
  wizRestartCamera: { de: 'Kamera neu starten', en: 'Restart camera' },
  wizCameraOff: { de: 'Kamera aus — Seriennummer eintippen.', en: 'Camera off — type the serial instead.' },
  wizCameraStarting: { de: 'Kamera wird gestartet…', en: 'Starting camera…' },
  wizCamLiveSerial: {
    de: 'Live · Barcode in den Rahmen halten',
    en: 'Live · point the camera at the barcode',
  },
  wizCamLiveTag: {
    de: 'Live · Xantory-QR in den Rahmen halten',
    en: 'Live · point the camera at the Xantory QR',
  },
  wizCamRead: { de: 'Gelesen · {code}', en: 'Read · {code}' },
  wizCamInsecure: {
    de: 'Die Kamera braucht hier eine sichere (HTTPS-)Verbindung — Code eintippen oder Handscanner nutzen.',
    en: 'The camera needs a secure (HTTPS) connection here — type the code or use a handheld reader.',
  },
  wizCamDenied: {
    de: 'Kamerazugriff wurde verweigert — im Browser erlauben oder den Code eintippen.',
    en: 'Camera permission was refused — allow it in the browser, or type the code.',
  },
  wizCamNone: {
    de: 'Keine Kamera gefunden — Code eintippen oder Handscanner nutzen.',
    en: 'No camera found — type the code or use a handheld reader.',
  },
  wizCamNoDecoder: {
    de: 'Dieser Browser kann mit der Kamera keine Codes lesen — Code eintippen.',
    en: 'This browser can’t read codes from the camera — type the code instead.',
  },
  wizStep2Heading: { de: 'Xantory-Tag scannen', en: 'Scan the Xantory tag' },
  wizStep2Lede: {
    de: 'Ein Label aus dem vorgedruckten Pool abziehen, auf die Einheit kleben und scannen — das verbindet Tag und Seriennummer. Die Klasse des Labels bestimmt die Klasse in Schritt 3.',
    en: 'Peel a label from the pre-printed pool, stick it on the unit, then scan it. That marries the tag to the serial. The label’s class decides the unit’s class in step 3.',
  },
  wizTagPrintedFor: {
    de: 'Gedruckt für Klasse {class} — Schritt 3 bietet nur Modelle dieser Klasse an. Für eine andere Klasse ein anderes Tag wählen.',
    en: 'Printed for class {class} — step 3 only offers models of this class. To register another class, pick a different tag.',
  },
  wizPoolNote: {
    de: '{n} freie Labels im Pool. Werden sie knapp? Einen weiteren Stapel drucken, ohne den Wareneingang zu verlassen.',
    en: '{n} unused labels in the pool. Running low? Print another batch without leaving intake.',
  },
  wizTagUnavailable: {
    de: 'Tag {tag} ist nicht verfügbar (Status: {state}).',
    en: 'Tag {tag} is not available (state: {state}).',
  },
  wizTagUnknown: {
    de: 'Tag {tag} ist in keinem Label-Pool bekannt.',
    en: 'Tag {tag} is not in any label pool.',
  },
  wizScannedTag: { de: 'Tag', en: 'Tag' },
  wizScannedSerial: { de: 'Seriennummer', en: 'Serial' },
  wizScannedLocked: {
    de: 'In Schritt 1–2 erfasst · zum Ändern zurückgehen',
    en: 'Captured in steps 1–2 · go Back to change',
  },
  wizSecProduct: { de: 'Produkt', en: 'Product' },
  wizSecWhere: { de: 'Wohin es kommt', en: 'Where it lands' },
  wizSecPurchase: { de: 'Einkauf', en: 'Purchase' },
  wizSecPurchaseHint: {
    de: 'Optional — ausfüllen, was auf den Papieren steht',
    en: 'Optional — fill what is on the paperwork',
  },
  wizSecSpecs: { de: 'Produktdaten', en: 'Product specs' },
  wizSecSpecsHint: {
    de: 'Von {model} — für jede Einheit gleich; am Modell ändern.',
    en: 'From {model} — the same on every unit; change them on the model.',
  },
  specsShowUnset: { de: '{n} nicht gesetzte anzeigen', en: 'Show {n} not set on the model' },
  specsHideUnset: { de: 'Nicht gesetzte ausblenden', en: 'Hide those not set' },
  specNotSet: { de: 'Am Modell nicht gesetzt', en: 'Not set on the model' },
  attrFromModel: { de: 'vom Modell', en: 'from model' },
  phIntakeSupplier: { de: 'z. B. Acme Vertrieb', en: 'e.g. Acme Distribution' },
  phIntakePoRef: { de: 'z. B. PO-2026-00123', en: 'e.g. PO-2026-00123' },
  phIntakeCost: { de: 'z. B. 250.00', en: 'e.g. 250.00' },
  phIntakeNotes: {
    de: 'Was die nächste Person über diese Einheit wissen sollte',
    en: 'Anything the next person should know about this unit',
  },
  // Footer notes — always say why the next step is (not) open.
  wizFoot1: { de: 'Bis Schritt 3 wird nichts gespeichert.', en: 'Nothing is saved until step 3.' },
  wizFoot1Auto: {
    de: 'Seriennummer erfasst — weiter zum Tag…',
    en: 'Serial captured — moving on to the tag…',
  },
  wizSimulateScan: { de: 'Scan simulieren', en: 'Simulate scan' },
  toastSerialScanned: {
    de: 'Seriennummer {serial} erfasst — als Nächstes das Tag scannen',
    en: 'Serial {serial} captured — scan the tag next',
  },
  phNewModelMfr: { de: 'z. B. Munters', en: 'e.g. Munters' },
  phNewModelNumber: { de: 'z. B. MLT800', en: 'e.g. MLT800' },
  phNewModelLife: { de: 'z. B. 10', en: 'e.g. 10' },
  wizFoot1Dup: {
    de: 'Blockiert — diese Seriennummer ist bereits im Register.',
    en: 'Blocked — this serial is already on the register.',
  },
  wizFoot1Wait: { de: 'Warte auf die Duplikatsprüfung.', en: 'Waiting for the duplicate check.' },
  wizFoot2: { de: 'Ein Tag scannen oder wählen, um weiterzugehen.', en: 'Scan or pick a tag to continue.' },
  wizFoot2Ready: {
    de: 'Tag gewählt — das Label wird erst beim Registrieren verbraucht.',
    en: 'Tag picked — the label is consumed only when you register.',
  },
  wizFoot3: {
    de: 'Eine Transaktion: das Asset, das gebundene Label und seine erste Bewegung.',
    en: 'One transaction: the asset, the label bound, and its first movement row.',
  },
  wizFoot3Missing: { de: 'Noch nötig: {what}.', en: 'Still needed: {what}.' },
  wizStartOverConfirmTitle: { de: 'Wareneingang neu beginnen?', en: 'Start this intake over?' },
  wizStartOverConfirmMsg: {
    de: 'Seriennummer, Tag und alle Angaben für diese Einheit werden verworfen.',
    en: 'The scanned serial, tag and every field for this unit are discarded.',
  },
  toastAssetRegistered: { de: '{tag} registriert — auf Lager', en: '{tag} registered — in stock' },
  toastModelCreateFailed: {
    de: 'Modell konnte nicht angelegt werden — nichts wurde gespeichert.',
    en: 'Could not add the model — nothing was saved.',
  },
  toastModelCreated: { de: 'Modell {model} angelegt', en: 'Model {model} added' },
  toastRegisterFailed: {
    de: 'Registrierung fehlgeschlagen — nichts wurde gespeichert.',
    en: 'Registration failed — nothing was saved.',
  },
  // Side panels
  unitTitle: { de: 'Diese Einheit', en: 'This unit' },
  unitNotSaved: { de: 'Noch nicht gespeichert', en: 'Not saved yet' },
  unitSerial: { de: 'Seriennummer', en: 'Serial' },
  unitTag: { de: 'Xantory-Tag', en: 'Xantory tag' },
  unitClass: { de: 'Klasse', en: 'Class' },
  unitModel: { de: 'Modell', en: 'Model' },
  unitLands: { de: 'Kommt nach', en: 'Lands in' },
  unitNoSerial: { de: 'keine · {reason}', en: 'none · {reason}' },
  runTitle: { de: 'Dieser Durchgang', en: 'This run' },
  runCount: { de: '{n} registriert', en: '{n} registered' },
  runKept: {
    de: 'Für die nächste Einheit aus diesem Karton beibehalten: {model}',
    en: 'Kept for the next unit out of this box: {model}',
  },
  runEmpty: { de: 'In diesem Besuch noch nichts registriert.', en: 'Nothing registered on this visit yet.' },
  runVisitOnly: {
    de: 'Nur dieser Besuch — die Liste leert sich beim Verlassen der Seite.',
    en: 'This visit only — the list clears when you leave the page.',
  },
  wizDenied: { de: 'Keine Berechtigung', en: 'Permission denied' },
  wizDeniedMsg: {
    de: 'Assets registrieren erfordert Schreibrechte auf dem Asset-Register.',
    en: 'Registering an asset needs write access to the asset register.',
  },
  navClasses: { de: 'Asset-Klassen', en: 'Asset Classes' },
  navModels: { de: 'Modelle (SKUs)', en: 'Models (SKUs)' },
  navLabels: { de: 'QR-Labels', en: 'QR Labels' },

  fieldManagedBy: { de: 'Verantwortlich', en: 'Managed by' },
  fieldManagedByHint: {
    de: 'Technisch verantwortliche Person — dorthin wird eine Störung geroutet. Nicht dasselbe wie „Zugewiesen an“: das ist, wer es gerade hat.',
    en: 'The technically responsible person — a fault routes there. Not the same as “Assigned to”, which is whoever is holding it.',
  },
  selectPerson: { de: 'Person wählen…', en: 'Pick a person…' },

  // R-235: Models (SKUs) as an Assets screen.
  modelsTitle: { de: 'Modelle (SKUs)', en: 'Models (SKUs)' },
  modelsDesc: {
    de: 'Der Katalog beschaffbarer Typen — „iPhone 14 Pro 256GB“ ist EIN Modell, die Geräte im Bestand sind viele Assets, die darauf zeigen. Global über alle Standorte, damit kein Standort eine SKU neu erfindet.',
    en: 'The catalogue of purchasable types — “iPhone 14 Pro 256GB” is one model, and the units you own are many assets pointing at it. Global across sites, so no site re-invents a SKU.',
  },
  modelsFilterChainNote: {
    de: 'Diese Filterkette nutzt die Beschaffung unverändert weiter: Klasse → Domäne (vorbelegt aus dem Standort der Anfrage) → Modelle. Deshalb erscheint HVAC nie in einer Büro-Anfrage — und ein Laptop trotzdem in einer Farm-Anfrage.',
    en: 'This filter chain is the one procurement reuses: class → domain (defaulted from the requester’s site) → models. That is why HVAC never appears for an office request, and why a laptop still appears for a farm one.',
  },
  newModel: { de: '+ Neues Modell', en: '+ New Model' },
  newModelTitle: { de: 'Neues Modell (SKU)', en: 'New model (SKU)' },
  editModelTitle: { de: 'Modell bearbeiten — {name}', en: 'Edit model — {name}' },
  modelSearchPh: { de: 'Modell / Hersteller suchen…', en: 'Search model / manufacturer…' },
  colModelName: { de: 'Modell / SKU', en: 'Model / SKU' },
  colModelNumber: { de: 'Modellnummer', en: 'Model number' },
  colOrderable: { de: 'Bestellbar', en: 'Orderable' },
  colUnitCost: { de: 'Stückkosten', en: 'Unit cost' },
  colStewardship: { de: 'Prüfstatus', en: 'Review' },
  approved: { de: 'geprüft', en: 'approved' },
  unapproved: { de: 'ungeprüft', en: 'unapproved' },
  fieldUom: { de: 'Mengeneinheit', en: 'Unit of measure' },
  filterOrderable: { de: 'Bestellbar: alle', en: 'Orderable: any' },
  orderableOnly: { de: 'Nur bestellbare', en: 'Orderable only' },
  notOrderable: { de: 'Nicht bestellbar', en: 'Not orderable' },
  notOrderableHint: {
    de: 'Für eine ausgelaufene SKU, von der noch Geräte im Bestand sind: verschwindet aus Anfragen und Wareneingang, die bestehenden Assets bleiben unberührt.',
    en: 'For a retired SKU you still own units of: it disappears from requests and intake, and its existing assets are untouched.',
  },
  modelClassHint: {
    de: 'Ein Modell sitzt unter einer Klasse, die keine Unterklassen hat — sie liefert das Attributschema und die Obergrenze der Domänen. Klassenwechsel lädt die Attributfelder neu.',
    en: 'A model sits under a class with nothing below it — that class supplies the attribute schema and the domain ceiling. Changing it reloads the attribute fields.',
  },
  modelMfrHint: {
    de: 'Wer es herstellt. Bewusst nicht der Lieferant: der Hersteller baut es, der Lieferant verkauft es, und der Lieferant wird erst auf der Bestellung erfasst.',
    en: 'Who makes it. Deliberately not the supplier: the manufacturer makes it, the supplier sells it, and the supplier is captured later on the purchase order.',
  },
  modelNumberHint: {
    de: 'Hersteller + Modellnummer sind zusammen der Dedup-Schlüssel — beide Hälften sind Pflicht, und die Datenbank lässt keine zweite lebende Zeile für dasselbe Produkt zu.',
    en: 'Manufacturer + model number together are the de-duplication key — both halves are required, and the database refuses a second live row for the same product.',
  },
  modelNameHint: {
    de: 'Alles aufnehmen, was es von seinen Geschwistern unterscheidet — Kapazität, Größe, Bundle.',
    en: 'Include whatever distinguishes it from its siblings — capacity, size, bundle.',
  },
  modelCostHint: {
    de: 'AED. Setzt die Schätzung auf einer Anfragezeile vor.',
    en: 'AED. Seeds the estimate on a request line.',
  },
  modelUomHint: {
    de: 'Die Einheit, auf die sich der Preis bezieht. Am MODELL, weil zwei SKUs einer Klasse unterschiedlich eingekauft werden können — ein Sensor stückweise, Kabel nach Meter.',
    en: 'The unit the price is per. On the MODEL because two SKUs of one class can be bought differently — a sensor each, cable by the metre.',
  },
  modelDomainsHint: {
    de: 'Leer lassen = Domänen der Klasse erben. Ein Modell darf sie nur einschränken, nie erweitern.',
    en: 'Leave empty to inherit the class’s domains. A model may only narrow them, never widen.',
  },
  modelOrderableHint: {
    de: 'Ausschalten für eine ausgelaufene SKU, von der noch Geräte im Bestand sind.',
    en: 'Turn off for a retired SKU you still own units of.',
  },
  modelOrderableLabel: {
    de: 'Erscheint in Anfragen und im Wareneingang',
    en: 'Appears in requests and intake',
  },
  modelSpecs: { de: 'Modell-Attribute', en: 'Model attributes' },
  modelSpecsHint: {
    de: 'Die Schlüssel kommen aus dem Wörterbuch der Klasse, weshalb es getippte Felder sind und keine JSON-Box: ein Schlüssel, der nirgends passt, lässt sich gar nicht eingeben. Nur Attribute mit „Liegt auf = Modell“ stehen hier — Asset-Attribute fragt der Wareneingang pro Einheit ab.',
    en: 'The keys come from the class dictionary, which is why these are typed fields and not a JSON box: a key that matches nothing cannot be typed at all. Only attributes whose “lives on” is Model appear here — asset-level ones are asked for at intake, per unit.',
  },
  modelNoSpecs: {
    de: 'Diese Klasse definiert keine Modell-Attribute. Bei Bedarf an der Klasse ergänzen.',
    en: 'This class defines no model-level attributes. Add them on the class if the SKU needs them.',
  },
  modelsNeedClassTitle: { de: 'Zuerst eine Asset-Klasse', en: 'An asset class is needed first' },
  modelsNeedClassMsg: {
    de: 'Ein Modell sitzt unter einer Klasse — sie definiert Attributschema und zulässige Domänen.',
    en: 'A model sits under a class — the class defines its attribute schema and which domains it may belong to.',
  },
  modelsEmptyTitle: { de: 'Keine passenden Modelle', en: 'No models match' },
  modelsEmptyMsg: {
    de: 'Die konkreten Konfigurationen anlegen, die eingekauft werden — Kapazität, Größe und alles, was Preis oder Bestellzeile ändert.',
    en: 'Add the specific configurations you buy — capacity, size, and anything else that changes the price or the purchase order line.',
  },
  deleteModelTitle: { de: 'Modell löschen — {name}', en: 'Delete model — {name}' },
  deleteModelHint: {
    de: 'Löschen geht nur, wenn kein Asset darauf zeigt UND es in keiner Alias-Kette steht. Alles mit Historie wird stattdessen ZUSAMMENGEFÜHRT — der Verlierer wird überlagert und aliasiert, nie gelöscht, damit Assets weiter auflösen.',
    en: 'Deleting is only possible when no asset points at it AND it is in no alias chain. Anything with history is MERGED instead — the loser is superseded and aliased, never deleted, so assets keep resolving.',
  },
  mergeInto: { de: 'Zusammenführen in', en: 'Merge into' },
  mergeIntoHint: {
    de: 'Die Zeile, die überlebt. Assets, die auf das hier gezeigte Produkt zeigen, werden über den Alias auf sie umgeleitet.',
    en: 'The row that survives. Assets pointing at this product get repointed to it through the alias.',
  },
  mergePick: { de: 'Überlebendes Produkt wählen…', en: 'Pick the surviving product…' },
  mergeAction: { de: 'Zusammenführen', en: 'Merge' },

  // R-235: Asset Classes — the taxonomy, editable at last.
  classesTitle: { de: 'Asset-Klassen', en: 'Asset Classes' },
  // D-149: the second sentence answers "why is + Model greyed out on this row?"
  // without anybody having to hover — the question got asked twice, which is the
  // signal that a tooltip was never going to be enough.
  classesDesc: {
    de: 'Die Taxonomie. Eine Klasse legt fest, wie Einheiten verfolgt werden, für welche Domänen sie zählen, welches Tag-Präfix sie tragen und welche Attribute ihre Modelle und Assets erben. Modelle hängen nur an Klassen OHNE Unterklassen — Zeilen mit Aufklapp-Pfeil sind Kategorien, in die man hineingeht, und „+ Modell" ist dort deaktiviert. „Inaktiv" ist der Ruhestand einer Klasse: die Klasse selbst wird stillgelegt, nicht ihre Assets — jede bereits registrierte Einheit bleibt unverändert und weiterhin lesbar, aber gegen die Klasse kann nichts Neues registriert werden, kein neues Modell darunter angelegt und kein Etikett erzeugt werden. Das ist der Weg für eine Klasse, die sich nicht mehr löschen lässt: sobald sie jemals eine Einheit getragen hat, verhindert deren Datensatz das Löschen dauerhaft — auch wenn die Einheit ausgemustert ist.',
    en: 'The taxonomy. A class declares how units are tracked, which domains they belong to, what tag prefix they carry, and the attribute set its models and assets inherit. Models hang off classes with nothing under them — a row with an expand arrow is a category you drill into, so "+ Model" is disabled there. "Inactive" is how a class retires: it takes the CLASS out of use, never its assets — every unit already registered is untouched and still readable, but nothing new can be registered against the class, no new model can hang off it, and no labels can be minted for it. That is the answer for a class you can no longer delete: once it has ever held a unit, that unit\'s record blocks the delete for good, even after the unit is retired.',
  },
  newClass: { de: '+ Neue Asset-Klasse', en: '+ New asset class' },
  newClassTitle: { de: 'Neue Asset-Klasse', en: 'New asset class' },
  editClassTitle: { de: 'Asset-Klasse bearbeiten — {code}', en: 'Edit asset class — {code}' },
  classCodeTaken: { de: 'Dieser Code existiert bereits.', en: 'That code already exists.' },
  classSearchPh: { de: 'Code oder Name suchen…', en: 'Search code or name…' },
  classCount: { de: '{n} von {total}', en: '{n} of {total}' },
  colClassCode: { de: 'Code', en: 'Code' },
  colLevel: { de: 'Ebene', en: 'Level' },
  colAssetKind: { de: 'Art', en: 'Type' },
  colDomains: { de: 'Domänen', en: 'Domains' },
  colTracking: { de: 'Verfolgung', en: 'Tracking' },
  colTagPrefix: { de: 'Tag-Präfix', en: 'Tag prefix' },
  colConnected: { de: 'Vernetzt', en: 'Connected' },
  colModels: { de: 'Modelle', en: 'Models' },
  unset: { de: 'nicht gesetzt', en: 'unset' },
  inherited: { de: 'geerbt', en: 'inherited' },
  addModel: { de: '+ Modell', en: '+ Model' },
  fieldNameEn: { de: 'Name (EN)', en: 'Name (EN)' },
  fieldNameDe: { de: 'Name (DE)', en: 'Name (DE)' },
  fieldParentClass: { de: 'Übergeordnete Klasse', en: 'Parent class' },
  noParent: { de: '— keine (oberste Ebene) —', en: '— none (top level) —' },
  classCodeHint: {
    de: 'Der Code IST die Identität und steckt später im Asset-Tag. Hierarchisch, wie bei Standorten: `D30.cool.fcu`. Nach dem Anlegen unveränderlich.',
    en: 'The code IS the identity and ends up inside the asset tag. Hierarchical, like locations: `D30.cool.fcu`. Immutable once created.',
  },
  // The code auto-derives for a type/subtype (parent + slug of the English name),
  // which is the convention all 458 live rows already follow. Group and class codes
  // are external standards codes and stay hand-typed.
  classCodeDerivedHint: {
    de: 'Wird aus übergeordneter Klasse + Name gebildet. Überschreibbar — dann bleibt der eingegebene Wert stehen. Nach dem Anlegen unveränderlich.',
    en: 'Built from the parent and the name. Type here to override — it then stays as you set it. Immutable once created.',
  },
  classCodeFromName: { de: 'wird aus dem Namen gebildet …', en: 'derived from the name…' },
  classNeedsParent: {
    de: 'Ein {level} ist ein Pfad unterhalb seiner übergeordneten Klasse — bitte eine auswählen.',
    en: 'A {level} is a path under its parent — pick one.',
  },
  classCodePrefix: {
    de: '⚠ Muss mit `{prefix}` beginnen, sonst findet die Einheit später kein Attribut-Wörterbuch und erhält einen eigenen Tag-Zähler.',
    en: '⚠ Must start with `{prefix}` — otherwise units of this class get no attribute dictionary and a tag counter of their own.',
  },
  classCodeSuggested: { de: 'Vorschlag: {code}', en: 'Suggested: {code}' },
  // D-147 — a group/class code IS the segment `class_of()` returns, so a dot in it
  // sends the attribute dictionary and the tag sequence to a different class.
  classCodeNoDot: {
    de: '⚠ Ein {level}-Code darf keinen Punkt enthalten — dieser würde als `{first}` aufgelöst und Wörterbuch sowie Tag-Zähler von dort beziehen.',
    en: '⚠ A {level} code cannot contain a dot — this one resolves to `{first}`, and would take its attribute dictionary and tag sequence from there.',
  },
  // D-149 — a model, and therefore a unit, hangs off a LEAF. A category with
  // classes under it is not a thing you can own one of.
  addModelNeedsLeaf: {
    de: 'Dieser {level} hat Klassen darunter — Modelle hängen an der konkreten Klasse, nicht an der Kategorie.',
    en: 'This {level} has classes under it — models hang off the specific class, not the category.',
  },
  modelClassNotLeaf: {
    de: '{class} hat Klassen darunter und kann daher kein Modell tragen. Bitte die konkrete Klasse wählen.',
    en: '{class} has classes under it, so it cannot carry a model. Pick the specific class instead.',
  },
  classLevelHint: {
    de: 'Wie tief die Klasse in der Taxonomie sitzt. Modelle hängen an einem Subtyp.',
    en: 'How deep the class sits in the taxonomy. Models hang off a subtype.',
  },
  classParentHint: {
    de: 'Optionale Hierarchie. Kinder erben Attribute und dürfen Domänen nur einschränken.',
    en: 'Optional hierarchy. Children inherit attributes and may only narrow domains.',
  },
  classKindHint: {
    de: 'Bestimmt die buchhalterische Behandlung. Einmal gewählt — eine Änderung später ist eine Migration.',
    en: 'Decides the financial treatment. Chosen once — changing it later is a migration.',
  },
  classDomainsHint: {
    de: 'Eignung, nicht Standort. HVAC ist nur Farm; ein Laptop ist beides, auch wenn er auf der Farm steht. Obergrenze der übergeordneten Klasse: {ceiling}.',
    en: 'Eligibility, not physical location. HVAC is farm-only; a laptop is both even when it sits at the farm. Parent ceiling: {ceiling}.',
  },
  classDomainsEmpty: {
    de: 'Mindestens eine Domäne — eine leere Liste heißt „von niemandem bestellbar“.',
    en: 'Pick at least one domain — an empty list reads as “orderable by nobody”.',
  },
  classDomainsWidened: {
    de: 'Ein Kind darf die Domänen der übergeordneten Klasse nur einschränken ({ceiling}).',
    en: 'A child may only narrow its parent’s domains ({ceiling}).',
  },
  classTrackingHint: {
    de: 'Serialisiert = ein Datensatz pro Einheit, mit Tag und Seriennummer. Mengenartikel werden im Lager gezählt, nicht etikettiert.',
    en: 'Serialized = one record per unit, with a tag and a serial. Quantity items are counted in a store, not tagged.',
  },
  classTrackingLocked: {
    de: '⚠ Nicht änderbar, sobald Assets unter dieser Klasse existieren — der Server weist es ab.',
    en: '⚠ Cannot change once assets exist under this class — the server refuses it.',
  },
  classPrefixHint: {
    de: 'Wird beim Erzeugen von QR-Label-Stapeln verwendet. Die Sequenz läuft pro Klasse.',
    en: 'Used when generating QR label batches. The sequence runs per class.',
  },
  classPrefixRequired: {
    de: 'Eine serialisierte Klasse braucht ein Tag-Präfix, sonst lassen sich für sie nie Labels erzeugen — und der Wareneingang scheitert ohne Erklärung.',
    en: 'A serialized class needs a tag prefix, or labels can never be generated for it — and intake fails with nothing to explain why.',
  },
  classCritHint: {
    de: 'Setzt die Vorbelegung für die AUSWIRKUNG einer Störung an einer Einheit dieser Klasse — die Hälfte der Prioritätsrechnung.',
    en: 'Defaults the IMPACT of an incident raised against a unit of this class, which is half of how its priority is computed.',
  },
  classConnected: { de: 'Vernetztes Gerät', en: 'Connected device' },
  classConnectedLabel: {
    de: 'Einheiten dieser Klasse senden Telemetrie',
    en: 'Units of this class report telemetry',
  },
  classConnectedHint: {
    de: 'Macht Einheiten in der Quarantäne-Liste verknüpfbar und lässt einen Gerätedatensatz erwarten.',
    en: 'Makes units bindable from the Unregistered queue and means a device record is expected.',
  },
  classCustody: { de: 'Zuständigkeit', en: 'Custody defaults' },
  classCustodyHint: {
    de: 'Zwei verschiedene Personen. Telefone haben einen Nutzer; feste Anlagen haben eine verantwortliche Person und gar keinen Nutzer — sie stehen einfach in einem Raum.',
    en: 'Two different people. Phones expect an end user; fixed plant expects a responsible person and no end user at all — it simply sits in a room.',
  },
  classExpectsEndUser: { de: 'Nutzer erwarten („Zugewiesen an“)', en: 'Expect an end user (“Assigned to”)' },
  classExpectsManagedBy: {
    de: 'Verantwortliche Person erwarten („Verantwortlich“)',
    en: 'Expect a responsible person (“Managed by”)',
  },
  classAttributes: { de: 'Attribute', en: 'Attributes' },
  classAttributesHint: {
    de: '„Liegt auf“ ist die Entscheidung, die den Katalog sauber hält: gleich für jede Einheit einer SKU → Modell. Pro Einheit unterschiedlich → Asset. Ändert sich im Betrieb → Gerätedatensatz.',
    en: '“Lives on” is the decision that keeps the catalogue clean: same for every unit of a SKU → Model. Differs per unit → Asset. Changes while the unit is running → Runtime.',
  },
  classAttrsAfterSave: {
    de: 'Attribute lassen sich anlegen, sobald die Klasse gespeichert ist — eine Definition hängt an einem Klassencode.',
    en: 'Attributes can be added once the class is saved — a definition hangs off a class code.',
  },
  classAttrDeleteWarning: {
    de: '⚠ Eine Definition zu löschen entfernt NICHT die bereits gespeicherten Werte. Die Site-Validierung weist den Schlüssel dann als unbekannt ab, wodurch jedes Asset dieser Klasse unspeicherbar wird, bis die Werte bereinigt sind. Nur für versehentlich angelegte Definitionen.',
    en: '⚠ Deleting a definition does NOT remove values already stored against it. Site validation then rejects the key as unknown, which makes every asset of that class unsaveable until those values are cleaned. For a definition created by mistake only.',
  },
  addAttribute: { de: '+ Attribut', en: '+ Add attribute' },
  attributeTitle: { de: 'Attribut', en: 'Attribute' },
  attrKey: { de: 'Schlüssel', en: 'Key' },
  attrKeyHint: {
    de: 'Der stabile Bezeichner, unter dem der Wert gespeichert wird — nach dem Anlegen unveränderlich.',
    en: 'The stable identifier the value is stored under — immutable once created.',
  },
  attrLabel: { de: 'Beschriftung', en: 'Label' },
  attrType: { de: 'Typ', en: 'Type' },
  attrUnit: { de: 'Einheit', en: 'Unit' },
  attrChoices: { de: 'Auswahlwerte', en: 'Choices' },
  attrChoicesHint: { de: 'Kommagetrennt.', en: 'Comma-separated.' },
  attrLivesOn: { de: 'Liegt auf', en: 'Lives on' },
  attrLivesOnHint: {
    de: 'Modell-Attribute füllt der Katalog; Asset-Attribute fragt der Wareneingang ab; Laufzeit-Attribute gehören zum Gerätedatensatz.',
    en: 'Model attributes are filled by the catalogue; asset attributes are asked for at intake; runtime attributes belong to the device record.',
  },
  attrMandatory: { de: 'Pflichtfeld', en: 'Mandatory' },
  attrMandatoryHint: {
    de: 'Der Wareneingang ist davon ausgenommen (D-140) — das Lager kann eine Installationsangabe nicht kennen.',
    en: 'Goods receipt is exempt (D-140) — the dock cannot know an installation fact.',
  },
  required: { de: 'Pflicht', en: 'Required' },
  livesOn_model: { de: 'Modell', en: 'Model' },
  livesOn_asset: { de: 'Asset', en: 'Asset' },
  livesOn_ci: { de: 'Laufzeit', en: 'Runtime' },
  assetKind_hardware: { de: 'Hardware', en: 'Hardware' },
  assetKind_consumable: { de: 'Verbrauchsmaterial', en: 'Consumable' },
  assetKind_facility: { de: 'Gebäudetechnik', en: 'Facility' },
  assetKind_software_licence: { de: 'Softwarelizenz', en: 'Software licence' },
  tracking_serialized: {
    de: 'Serialisiert — ein Datensatz pro Einheit',
    en: 'Serialized — one record per unit',
  },
  tracking_quantity: { de: 'Menge — im Lager gezählt', en: 'Quantity — counted in a store' },
  tracking_not_tracked: { de: 'Nicht verfolgt', en: 'Not tracked' },
  classesEmptyTitle: { de: 'Noch keine Asset-Klassen', en: 'No asset classes yet' },
  classesEmptyMsg: {
    de: 'Mit den Kategorien beginnen, die das Team tatsächlich beschafft und wartet. Jede Klasse wird im Wareneingang und später in einer Beschaffungsanfrage auswählbar.',
    en: 'Start with the categories your team actually buys and maintains. Each class becomes selectable in intake and, later, in a procurement request.',
  },
  deleteClassTitle: { de: 'Asset-Klasse löschen — {code}', en: 'Delete asset class — {code}' },
  deleteClassConfirm: {
    de: '{code} löschen? Nichts hängt daran.',
    en: 'Delete {code}? Nothing hangs off it.',
  },
  deleteClassBlockedIntro: {
    de: 'Löschen nicht möglich — daran hängt noch Folgendes:',
    en: 'Cannot delete — these still hang off it:',
  },
  deleteClassBlockedHint: {
    de: 'Zwei dieser Abhängigkeiten würden in der Datenbank mitgelöscht (PM-Pläne, KPI-Benchmarks), deshalb wird hier vorher gefragt statt hinterher zu scheitern.',
    en: 'Two of these would be CASCADE-deleted by the database (PM plans, KPI benchmarks), which is why this asks first rather than failing after.',
  },
  // ⚠ The honest alternative, and it replaced advice that pointed at a control
  // which did not exist ("retire the class as inactive" — there was no such
  // switch anywhere in the console).
  deleteClassShelveInstead: {
    de: 'Ein Asset wird nie gelöscht — auch ein ausgemustertes zählt hier mit. Eine Klasse, die jemals eine Einheit getragen hat, lässt sich daher nicht mehr löschen. {code} stattdessen auf INAKTIV setzen: das betrifft nur die Klasse, nicht ihre Assets — bestehende Einheiten bleiben unverändert und lesbar, aber die Klasse nimmt keine Registrierungen, keine neuen Modelle und keine Etiketten mehr an.',
    en: 'An asset is never deleted — a retired one still counts here — so a class that has ever held a unit can no longer be deleted at all. Mark {code} INACTIVE instead: that affects only the class, not its assets — existing units stay unchanged and readable, but the class accepts no registrations, no new models and no labels.',
  },
  blockerOpen: { de: 'Öffnen', en: 'Open' },
  // Where a blocker deliberately has no destination, and why. Saying so beats a
  // link that lands somewhere useless.
  blockerNoScreen_attributes: {
    de: 'im Reiter „Attribute" dieser Klasse bearbeiten',
    en: "edit in this class's Attributes section",
  },
  'blockerNoScreen_PM plans': {
    de: 'noch kein Bildschirm für den PM-Katalog',
    en: 'no PM catalogue screen yet',
  },
  markClassInactive: { de: 'Als inaktiv markieren', en: 'Mark inactive' },
  dependant_subclasses: { de: 'Unterklassen', en: 'subclass(es)' },
  dependant_models: { de: 'Modelle', en: 'model(s)' },
  dependant_attributes: { de: 'Attribute', en: 'attribute(s)' },
  dependant_assets: { de: 'Assets', en: 'asset(s)' },
  'dependant_PM plans': { de: 'PM-Pläne', en: 'PM plan(s)' },
  'dependant_KB articles': { de: 'KB-Artikel', en: 'KB article(s)' },
  'dependant_change templates': { de: 'Change-Vorlagen', en: 'change template(s)' },
  dependant_problems: { de: 'Probleme', en: 'problem(s)' },
  'dependant_location tokens': { de: 'Standort-Tokens', en: 'location token(s)' },
  'dependant_KPI benchmarks': { de: 'KPI-Benchmarks', en: 'KPI benchmark(s)' },

  // R-235: the state a class gets instead of a delete it can never have.
  //
  // ⚠⚠ Called INACTIVE, and "shelved" was RENAMED to it after the user asked what it
  // meant. The word was wrong in this product specifically, and the proof is on this very
  // screen: **FOUR live asset classes are literally NAMED "shelving"** — `Stainless steel
  // shelving`, `Bookcase / open shelving`, `Heavy-duty storage shelving (dry store)`,
  // `Lockable seed cabinets / shelving`. A badge reading "Shelved" beside a class called
  // *Stainless steel shelving* says nothing. `shelving` also means physical racking
  // elsewhere here ("nursery shelving") and `shelf life` means produce freshness.
  // The DB column is `active`; the label now matches it.
  // Models — examples on the create form, and every write reports its outcome.
  phModelMfr: { de: 'z. B. Apple', en: 'e.g. Apple' },
  phModelNumber: { de: 'z. B. A2650', en: 'e.g. A2650' },
  phModelName: { de: 'z. B. iPhone 14 Pro', en: 'e.g. iPhone 14 Pro' },
  phModelCost: { de: 'z. B. 999', en: 'e.g. 999' },
  phModelUom: { de: 'z. B. each', en: 'e.g. each' },
  toastModelSaved: { de: 'Modell erfolgreich gespeichert', en: 'Model saved successfully' },
  toastModelDeleted: { de: 'Modell erfolgreich gelöscht', en: 'Model deleted successfully' },
  toastModelsMerged: { de: 'Modelle erfolgreich zusammengeführt', en: 'Models merged successfully' },
  // Asset Classes — every write reports its outcome, and names what it touched.
  toastClassCreated: { de: 'Klasse {code} erfolgreich angelegt', en: 'Class {code} created successfully' },
  toastClassUpdated: {
    de: 'Klasse {code} erfolgreich aktualisiert',
    en: 'Class {code} updated successfully',
  },
  toastClassDeleted: { de: 'Klasse {code} erfolgreich gelöscht', en: 'Class {code} deleted successfully' },
  toastClassShelved: { de: 'Klasse {code} auf inaktiv gesetzt', en: 'Class {code} marked inactive' },
  toastAttrSaved: { de: 'Attribut {key} erfolgreich gespeichert', en: 'Attribute {key} saved successfully' },
  toastAttrDeleted: { de: 'Attribut {key} erfolgreich gelöscht', en: 'Attribute {key} deleted successfully' },
  // A destructive action names the thing it is about to destroy.
  confirmDeleteAttr: {
    de: 'Attribut {key} wirklich löschen?',
    en: 'Are you sure you want to delete attribute {key}?',
  },
  // Examples on the class and attribute forms, never a repeat of the label.
  phClassNameEn: { de: 'z. B. Umluftventilator', en: 'e.g. Circulation fan' },
  phClassNameDe: { de: 'z. B. Umluftventilator', en: 'e.g. Umluftventilator' },
  phAttrKey: { de: 'z. B. voltage', en: 'e.g. voltage' },
  phAttrLabel: { de: 'z. B. Spannung', en: 'e.g. Voltage' },
  phAttrUnit: { de: 'z. B. V', en: 'e.g. V' },
  classInactive: { de: 'Inaktiv', en: 'Inactive' },
  classLifecycle: { de: 'Lebenszyklus', en: 'Lifecycle' },
  classActiveLabel: { de: 'Aktiv', en: 'Active' },
  classInactiveLabel: { de: 'Inaktiv', en: 'Inactive' },
  classActiveHint: {
    de: 'Betrifft die KLASSE, nicht ihre Assets. Eine inaktive Klasse bleibt in der Taxonomie und auf jedem Asset lesbar, das sie trägt — bereits registrierte Einheiten ändern sich nicht —, nimmt aber keine neuen Einheiten, keine neuen Modelle und keine Etiketten mehr an. Das ist der Weg für eine Klasse, die sich nicht mehr löschen lässt.',
    en: 'This is about the CLASS, not its assets. An inactive class stays in the taxonomy and stays readable on every asset that carries it — units already registered do not change — but it accepts no new units, no new models and no labels. It is the answer for a class you can no longer delete.',
  },
  classInactiveWarn: {
    de: 'Nach dem Speichern: keine Registrierung gegen diese Klasse, kein neues Modell darunter, keine Etiketten. Bestehende Assets bleiben unberührt. Ist es das letzte aktive Kind, wird die Elternklasse wieder selbst registrierbar.',
    en: 'On save: nothing can be registered against this class, no new model can hang off it, and no labels can be minted. Existing assets are untouched. If it is the last active child, its parent becomes registerable itself.',
  },
  addModelNeedsActive: {
    de: 'Diese Klasse ist inaktiv — gegen sie kann keine Einheit registriert werden, ein Modell darunter wäre also nicht nutzbar.',
    en: 'This class is inactive — no unit can be registered against it, so a model under it could never be used.',
  },
  hideInactive: { de: 'Inaktive ausblenden ({n})', en: 'Hide inactive ({n})' },
  // The prototype's class-modal `extra` button (V8:3665), which the port dropped.
  classModelsOfThis: { de: 'Modelle dieser Klasse', en: 'Models of this class' },

  // R-235: the Scan screen's last-scan card.
  colTelemetry: { de: 'Telemetrie', en: 'Telemetry' },
  noDeviceRecord: { de: 'kein Gerätedatensatz', en: 'no device record' },
  flagFault: { de: 'Störung melden', en: 'Flag fault' },
  flagFaultHint: {
    de: 'Öffnet denselben Bewegungsdialog — eine vom Feld gemeldete Störung schreibt dieselbe Journalzeile wie eine vom Schreibtisch.',
    en: 'Opens the same Move dialog — a fault flagged from the floor writes the same ledger row as one flagged from a desk.',
  },

  // R-235: the QR label pool, three levels deep.
  labelsPoolTitle: { de: 'QR-Labels', en: 'QR Labels' },
  labelsPoolDesc: {
    de: 'Der vorgedruckte Label-Pool. Pro Klassenpräfix einen Stapel erzeugen, Blatt drucken — die Labels warten am Wareneingang darauf, einer Seriennummer zugeordnet zu werden.',
    en: 'The pre-printed label pool. Generate a batch per class prefix, print the sheet, and the labels wait at the dock to be matched to a serial during intake.',
  },
  labelsTitleClass: { de: 'QR-Labels · {code}', en: 'QR Labels · {code}' },
  labelsTitleBatch: { de: 'QR-Labels · {range}', en: 'QR Labels · {range}' },
  labelsSheetHint: {
    de: 'Abziehen, aufkleben, scannen. Ein Label bedeutet nichts, bis der Wareneingang es einer Seriennummer zuordnet — ein VERKNÜPFTES Label öffnet das Gerät, auf dem es gelandet ist.',
    en: 'Peel, stick, scan. A label means nothing until intake binds it to a serial — a BOUND label opens the asset it ended up on.',
  },
  labelsGenerated: { de: 'Labels erzeugt', en: 'Labels generated' },
  colUnusedInPool: { de: 'Frei im Pool', en: 'Unused in pool' },
  colBound: { de: 'Verknüpft', en: 'Bound' },
  colVoided: { de: 'Verworfen', en: 'Voided' },
  colTagRange: { de: 'Tag-Bereich', en: 'Tag range' },
  colLabels: { de: 'Labels', en: 'Labels' },
  colSize: { de: 'Größe', en: 'Size' },
  colBreakdown: { de: 'Aufteilung', en: 'Breakdown' },
  noneLeft: { de: 'keine übrig', en: 'none left' },
  generateBatch: { de: '+ Stapel erzeugen', en: '+ Generate batch' },
  generate: { de: 'Erzeugen', en: 'Generate' },
  generateClassHint: {
    de: 'Präfix und Sequenz kommen aus der Klasse, nicht von diesem Bildschirm.',
    en: 'Prefix and sequence come from the class, not from this screen.',
  },
  generateCountHint: {
    de: 'Maximal 500. ⚠ Der Zähler läuft nur vorwärts — erzeugte Nummern kommen nie zurück, deshalb steht die Vorschau darunter.',
    en: 'Maximum 500. ⚠ The counter only runs forward — generated numbers never come back, which is what the preview below is for.',
  },
  fieldCount: { de: 'Anzahl', en: 'Quantity' },
  fieldLabelSize: { de: 'Label-Größe', en: 'Label size' },
  labelSize_38x19: { de: '38 × 19 mm — Geräte', en: '38 × 19 mm — equipment' },
  labelSize_50x25: { de: '50 × 25 mm — Dach / außen', en: '50 × 25 mm — rooftop / outdoor' },
  labelSize_25x12: { de: '25 × 12 mm — kleine Sensoren', en: '25 × 12 mm — small sensors' },
  sequencePreview: { de: 'Als Nächstes in der Sequenz:', en: 'Next in sequence:' },
  printSheet: { de: 'Blatt drucken', en: 'Print sheet' },
  voidLabel: { de: 'Verwerfen', en: 'Void' },
  labelState_unused: { de: 'frei', en: 'unused' },
  labelState_bound: { de: 'verknüpft', en: 'bound' },
  labelState_void: { de: 'verworfen', en: 'voided' },
  // ─── R-239: the sheet reads its own state off the tiles ────────────────────
  labelStateAll: { de: 'Status: alle', en: 'State: all' },
  labelsLegend: {
    de: 'gestrichelt = frei · blass = verworfen · ein verknüpftes Label öffnet seine Anlage',
    en: 'dashed = unused · faded = voided · a bound label opens its asset',
  },
  labelsOpenAsset: { de: '{tag} öffnen', en: 'Open {tag}' },
  labelBoundOn: { de: 'verknüpft am {when} — öffnen', en: 'bound {when} — open the unit' },
  noLabelsInStateTitle: { de: 'Keine Labels in diesem Status', en: 'No labels in that state' },
  noLabelsInStateMsg: {
    de: 'Dieser Stapel enthält keine Labels mit dem gewählten Status. Filter zurücksetzen, um das ganze Blatt zu sehen.',
    en: 'This batch has no labels in the selected state. Clear the filter to see the whole sheet.',
  },
  findTag: { de: 'Suchen', en: 'Find' },
  findTagPh: { de: 'Zu einem Tag springen — z. B. SEN-0043', en: 'Jump to a tag — e.g. SEN-0043' },
  poolEmptyForClass: {
    de: 'Keine freien Labels für {code}. Der Wareneingang kann für diese Klasse nichts registrieren, bis ein neuer Stapel erzeugt ist — das Tag kommt aus dem Pool.',
    en: 'No labels left in the pool for {code}. Intake cannot register a unit of this class until a new batch is generated — the tag comes from the pool.',
  },
  labelsEmptyTitle: { de: 'Noch keine Labels erzeugt', en: 'No labels generated yet' },
  labelsPoolEmptyMsg: {
    de: 'Einen Stapel erzeugen, drucken und die Labels beim Eintreffen aufkleben.',
    en: 'Generate a batch, print it, and stick the labels on units as they arrive.',
  },
  noBatchesTitle: { de: 'Keine Stapel für diese Klasse', en: 'No batches for this class' },
  noBatchesMsg: {
    de: 'Ein Stapel ist ein gedrucktes Blatt. Erzeugen, drucken, verwenden.',
    en: 'One batch is one printed sheet. Generate it, print it, use it.',
  },

  // R-235: the Unregistered-device quarantine.
  unregTitle: { de: 'Unregistrierte Geräte', en: 'Unregistered devices' },
  unregDesc: {
    de: 'Quarantäne. Geräte, die im Netz gesehen wurden und zu denen niemand ein Asset registriert hat. Sie werden NICHT automatisch angelegt — jemand verknüpft sie mit einem Asset oder weist sie ab.',
    en: 'Quarantine. Devices seen on the network that nobody registered as an asset. They are NOT auto-created — someone binds them to an asset or rejects them.',
  },
  unregWhy: {
    de: 'Dieser Bildschirm macht „das Asset ist der Master“ real. Farm-Geräte melden sich im Netz, ob registriert oder nicht. Könnte die Erfassung selbst Datensätze anlegen, wäre das Register in Monaten voller Phantomgeräte und niemand würde ihm mehr trauen. Jede Ankunft landet hier, und ein Mensch entscheidet.',
    en: 'This screen is what makes “asset is the master” real. Farm devices appear on the network whether or not anyone registered them. If ingest could auto-create records, the register would fill with phantom devices within months and nobody would trust it. Every arrival lands here and a human decides.',
  },
  // The match-precedence note under the Unregistered table, one key per point so it can be a list.
  matchPrecedenceTitle: { de: 'Zuordnungsreihenfolge', en: 'Match precedence' },
  matchRule1Name: { de: 'Seriennummer', en: 'Serial' },
  matchRule1Desc: { de: 'verbindlich und unveränderlich.', en: 'authoritative and immutable.' },
  matchRule2Name: { de: 'Geräte-ID (MAC)', en: 'Device ID (MAC)' },
  matchRule2Desc: {
    de: 'Netzidentität, erkennt ein früher verknüpftes Gerät auch nach Firmware-Reset.',
    en: 'network identity; matches a previously bound device even after a firmware reset.',
  },
  matchRule3Name: { de: 'Asset-Tag', en: 'Asset tag' },
  matchRule3Desc: {
    de: 'der menschliche Schlüssel, wenn beim Verknüpfen das Label gescannt wird.',
    en: 'the human key, used when an operator scans the label while binding.',
  },
  matchNever: {
    de: 'Niemals: aus einem gefundenen Gerät ein Asset erzeugen.',
    en: 'Never: auto-create an asset from a discovered device.',
  },
  matchDormant: {
    de: '⚠ Arm 1 ist derzeit inaktiv — unsere Inventarmeldung überträgt keine Seriennummer.',
    en: '⚠ Arm 1 is dormant today — our inventory wire carries no serial.',
  },
  tabPending: { de: 'Offen', en: 'Pending' },
  tabBound: { de: 'Verknüpft', en: 'Bound' },
  tabRejected: { de: 'Abgewiesen', en: 'Rejected' },
  colDeviceRef: { de: 'Geräte-ID (MAC)', en: 'Device ID (MAC)' },
  colReportedSerial: { de: 'Gemeldete Seriennr.', en: 'Reported serial' },
  colFirmware: { de: 'Firmware', en: 'Firmware' },
  colChannels: { de: 'Kanäle', en: 'Channels' },
  colLastSeen: { de: 'Zuletzt gesehen', en: 'Last seen' },
  colSuggested: { de: 'Vorschlag', en: 'Suggested match' },
  noneReported: { de: 'keine gemeldet', en: 'none reported' },
  noMatch: { de: 'Keine Zuordnung — neu registrieren?', en: 'No match — register as new?' },
  rejected: { de: 'abgewiesen', en: 'rejected' },
  bindDevice: { de: 'Verknüpfen', en: 'Bind' },
  bindDeviceTo: { de: 'Verknüpfen mit…', en: 'Bind to…' },
  registerFromDevice: { de: 'Registrieren…', en: 'Register…' },
  rejectDevice: { de: 'Abweisen', en: 'Reject' },
  reopenDevice: { de: 'Zurück auf offen', en: 'Move to pending' },
  bindTitle: { de: '{ref} verknüpfen', en: 'Bind {ref}' },
  bindHint: {
    de: 'Ein Geräte-Datensatz gehört zu genau EINEM Asset und kann nicht verschoben werden — eine falsche Verknüpfung meldet eine echte Störung am falschen Gerät.',
    en: 'A device record belongs to exactly ONE asset and cannot be moved — a wrong bind reports a real fault against the wrong unit.',
  },
  alreadyBound: { de: 'bereits verknüpft mit {ref}', en: 'already bound to {ref}' },
  rejectTitle: { de: '{ref} abweisen', en: 'Reject {ref}' },
  rejectHint: {
    de: 'Bleibt als Nachweis in der Liste der abgewiesenen Geräte — nützlich, wenn sich herausstellt, dass jemand ein Testgerät im Netz vergessen hat.',
    en: 'It stays in the rejected list as evidence — useful when it turns out to be a test rig someone left on the network.',
  },
  rejectNote: { de: 'Grund', en: 'Reason' },
  rejectNotePh: { de: 'z. B. Testgerät im Labor', en: 'e.g. test rig in the lab' },
  unregLoadError: {
    de: 'Geräteliste nicht abrufbar',
    en: 'Could not read the device inventory',
  },
  unregEmpty_pending: { de: 'Nichts in Quarantäne', en: 'Nothing in quarantine' },
  unregEmpty_bound: { de: 'Nichts verknüpft', en: 'Nothing bound' },
  unregEmpty_rejected: { de: 'Nichts abgewiesen', en: 'Nothing rejected' },
  unregEmptyMsg: {
    de: 'Meldet die MQTT-Erfassung ein Gerät, das zu keinem registrierten Asset gehört, erscheint es hier. Automatisch wird nichts angelegt.',
    en: 'When MQTT ingest sees a device that is not bound to a registered asset, it appears here. Nothing is created automatically.',
  },

  // R-235: the Transfers LEDGER screen.
  transfersTitle: { de: 'Bewegungen', en: 'Transfers' },
  transfersDesc: {
    de: 'Das Bewegungsjournal — nur Anfügen. Jede Änderung von Zustand oder Standort schreibt hier eine Zeile, damit „wo ist es jetzt“ und „wo war es“ beide beantwortbar sind.',
    en: 'The movement ledger — append-only. Every placement or state change writes a row here, so “where is it now” and “where has it been” are both answerable.',
  },
  transfersNoApproval: {
    de: 'Kein Genehmigungsschritt, per Entscheidung. Eine Bewegung durch eine berechtigte Person wirkt sofort, und die Zeile hält fest, wer und wann. Genehmigung pro Klasse für hochwertige Geräte lässt sich später ergänzen, ohne das Journal zu ändern.',
    en: 'No approval step, by decision. A move by an authorised user takes effect immediately and the row records who and when. Per-class approval for high-value items can be layered on later without changing the ledger.',
  },
  newTransfer: { de: '+ Neue Bewegung', en: '+ New transfer' },
  pickAssetToMove: { de: 'Gerät wählen…', en: 'Pick an asset…' },
  pickAssetTitle: { de: 'Gerät für die Bewegung wählen', en: 'Pick an asset to move' },
  colReference: { de: 'Referenz', en: 'Reference' },
  colAsset: { de: 'Gerät', en: 'Asset' },
  colFrom: { de: 'Von', en: 'From' },
  colTo: { de: 'Nach', en: 'To' },
  colReason: { de: 'Grund', en: 'Reason' },
  colStateChange: { de: 'Zustandswechsel', en: 'State change' },
  colMovedBy: { de: 'Bewegt von', en: 'Moved by' },
  colWhen: { de: 'Wann', en: 'When' },
  colLinked: { de: 'Verknüpft', en: 'Linked' },
  fromNew: { de: '— (neu)', en: '— (new)' },
  placementUnchanged: { de: 'unverändert', en: 'unchanged' },
  actorService: { de: 'Dienst', en: 'service' },
  filterAllReasons: { de: 'Alle Gründe', en: 'Reason: all' },
  filterAllMovers: { de: 'Alle Personen', en: 'Moved by: anyone' },
  filterInTransit: { de: 'Nur unterwegs', en: 'In transit only' },
  noMovementsTitle: { de: 'Keine Bewegungen erfasst', en: 'No movements recorded' },
  noMovementsMsg: {
    de: 'Zeilen erscheinen automatisch, sobald sich Lagerort, Standort oder Zustand eines Geräts ändern — die erste Registrierung eingeschlossen.',
    en: 'Rows appear automatically whenever an asset’s store, location or state changes — including its first registration.',
  },

  movementHistory: { de: 'Bewegungshistorie', en: 'Movement history' },
  noMovements: {
    de: 'Noch keine Bewegungen. Eine Zeile entsteht automatisch, sobald sich Zustand oder Standort ändern — die Registrierung eingeschlossen.',
    en: 'No movements yet. A row appears automatically whenever the state or the placement changes — the registration included.',
  },

  // R-235: the record's action toolbar — one Move button, plus the guided flows
  // that collect evidence a generic move cannot ask for.
  moveTransfer: { de: 'Bewegen / Umbuchen', en: 'Move / Transfer' },
  deployGuided: { de: 'In Betrieb nehmen (geführt)', en: 'Put into service (guided)' },
  retireGuided: { de: 'Stilllegen', en: 'Retire' },
  disposeGuided: { de: 'Entsorgung bestätigen', en: 'Confirm disposal' },

  // R-235: THE Move dialog (the prototype's moveAsset).
  moveTitle: { de: 'Bewegen — {tag}', en: 'Move — {tag}' },
  moveCurrently: { de: 'Derzeit {state} · {where}.', en: 'Currently {state} · {where}.' },
  inStore: { de: 'im Lager', en: 'in a store' },
  notPlaced: { de: 'kein Standort', en: 'not placed' },
  moveToState: { de: 'Resultierender Zustand', en: 'Resulting state' },
  moveToStateHint: {
    de: 'Nur Übergänge, die der Lebenszyklus ab {state} erlaubt.',
    en: 'Only transitions the lifecycle permits from {state}.',
  },
  moveToStockroom: { de: 'In welches Lager', en: 'To which store' },
  moveToStockroomHint: {
    de: 'Bestand liegt in einem Lagerort — deshalb sind hier nur Lagerorte wählbar.',
    en: 'Stock sits in a stockroom, which is why only stockrooms are listed here.',
  },
  moveToLocation: { de: 'An welchen Standort', en: 'To which location' },
  moveToLocationHint: {
    de: 'Ein Gerät im Einsatz steht an einem echten Standort, nicht im Lager.',
    en: 'A unit in service stands at a real location, not in a store.',
  },
  moveToFlexHint: {
    de: 'Beides erlaubt: zur Reparatur weggeschickt (Lager) oder vor Ort instand gesetzt (Standort).',
    en: 'Either is legal: sent away for repair (a store) or repaired where it stands (a location).',
  },
  movePickPlace: { de: 'Ort wählen…', en: 'Pick the place…' },
  movePlacementUnchanged: {
    de: 'Der Standort bleibt unverändert — „wo war es, als es verloren ging“ ist die Frage, die dieser Zustand beantwortet.',
    en: 'Placement is left unchanged — “where was it when we lost it” is the question this state answers.',
  },
  moveReason: { de: 'Grund', en: 'Reason' },
  moveReasonAuto: { de: 'Automatisch (aus dem Übergang)', en: 'Automatic (from the move)' },
  moveReasonHint: {
    de: 'Leer lassen ist in Ordnung: der Grund folgt dem gewählten Übergang.',
    en: 'Leaving it empty is fine — the reason follows the move you picked.',
  },
  moveLinked: { de: 'Verknüpfter Vorgang', en: 'Linked record' },
  moveLinkedHint: {
    de: 'Auftrag, Störung oder Lieferschein, der die Bewegung ausgelöst hat.',
    en: 'The work order, incident or receipt that triggered the move.',
  },
  moveLinkedPh: { de: 'z. B. WO-1042', en: 'e.g. WO-1042' },
  moveNotes: { de: 'Notiz', en: 'Notes' },
  moveNotesPh: {
    de: 'Was die nächste Person wissen muss',
    en: 'Anything the next person needs to know',
  },
  moveSubmit: { de: 'Bewegen', en: 'Move asset' },
  moveReservedRefused: {
    de: 'Dieses Gerät ist für einen genehmigten Change reserviert und kann nicht von Hand bewegt werden. Den Change freigeben oder abbrechen — nicht daran vorbei arbeiten.',
    en: 'This unit is reserved for an approved change and cannot be moved by hand. Release or cancel that change — do not move around it.',
  },
  moveTerminal: {
    de: '{state} ist ein Endzustand — es gibt nichts zu bewegen.',
    en: '{state} is terminal — there is nothing to move.',
  },

  // R-144: stockroom sub-status.
  colSubstatus: { de: 'Lager-Status', en: 'Sub-status' },
  subDefective: { de: 'Defekt', en: 'Defective' },
  subPendingDisposal: { de: 'Entsorgung ausstehend', en: 'Pending disposal' },
  filterAllSub: { de: 'Alle Lager-Status', en: 'All sub-statuses' },

  // R-144: deploy from stock (custodian + location + photo/signature gates).
  deploy: { de: 'In Einsatz nehmen', en: 'Deploy' },
  deployTitle: { de: 'Deploy — {tag}', en: 'Deploy — {tag}' },
  deployDesc: {
    de: 'Foto und Zielstandort sind Pflicht; bei persönlicher Übergabe zusätzlich eine Unterschrift.',
    en: 'A photo and a destination location are required; a personal handover also needs a signature.',
  },
  deployCustodian: { de: 'Verantwortliche Person (optional)', en: 'Custodian (optional)' },
  deployCustodianPh: { de: 'Person wählen…', en: 'Select a person…' },
  deployCustodianNone: { de: 'Kein Custodian (Standort-Deploy)', en: 'No custodian (location deploy)' },
  deployLocation: { de: 'Zielstandort', en: 'Destination location' },
  deployPhoto: { de: 'Foto (Pflicht)', en: 'Photo (required)' },
  deployPhotoHint: {
    de: 'Foto des installierten Geräts — Kamera oder Datei.',
    en: 'A photo of the installed unit — camera or file.',
  },
  deployTakePhoto: { de: 'Foto aufnehmen', en: 'Take photo' },
  deployPhotoTaken: { de: 'Foto ausgewählt: {name}', en: 'Photo selected: {name}' },
  deploySignature: { de: 'Übergabe-Unterschrift', en: 'Handover signature' },
  deploySignatureHint: {
    de: 'Bei persönlicher Übergabe erforderlich.',
    en: 'Required for a personal handover.',
  },
  clearSignature: { de: 'Unterschrift löschen', en: 'Clear signature' },
  deploySubmit: { de: 'Deploy bestätigen', en: 'Confirm deploy' },
  deployNeedPhoto: { de: 'Bitte zuerst ein Foto hinzufügen.', en: 'Add a photo first.' },
  deployNeedLocation: { de: 'Bitte einen Zielstandort wählen.', en: 'Choose a destination location.' },
  deployNeedSignature: {
    de: 'Bei einem Custodian ist eine Unterschrift erforderlich.',
    en: 'A signature is required when a custodian is set.',
  },
  deployUploading: { de: 'Wird hochgeladen…', en: 'Uploading…' },
  // A-7 handover checklist (handbook K.6).
  handoverChecklist: { de: 'Übergabe-Checkliste', en: 'Handover checklist' },
  handoverChecklistHint: {
    de: 'Bei persönlicher Übergabe eines IT-Geräts: alle Punkte bestätigen.',
    en: 'For a personal handover of an IT device: confirm every item.',
  },
  handoverItemConfigured: {
    de: 'Gerät konfiguriert & verschlüsselt',
    en: 'Device configured & encrypted',
  },
  handoverItemSoftware: { de: 'Standardsoftware installiert', en: 'Standard software applied' },
  handoverIncomplete: {
    de: 'Bitte alle Punkte der Übergabe-Checkliste bestätigen.',
    en: 'Please confirm every item on the handover checklist.',
  },

  // K-9/K-10: retire / dispose structured fields.
  terminalConfirmMatch: { de: 'Zur Bestätigung {tag} eingeben', en: 'Type {tag} to confirm' },
  retirementReason: { de: 'Stilllegungsgrund', en: 'Retirement reason' },
  disposalMethod: { de: 'Entsorgungsmethode', en: 'Disposal method' },
  disposalMethod_certified_recycler: { de: 'Zertifizierter Recycler', en: 'Certified recycler' },
  disposalMethod_resale: { de: 'Weiterverkauf', en: 'Resale' },
  disposalMethod_return_to_vendor: { de: 'Rückgabe an Lieferant', en: 'Return to vendor' },
  disposalMethod_scrap: { de: 'Verschrottung', en: 'Scrap' },
  disposalMethod_donation: { de: 'Spende', en: 'Donation' },
  disposalVendor: { de: 'Entsorger', en: 'Disposal vendor' },
  disposalDate: { de: 'Entsorgungsdatum', en: 'Disposal date' },

  // C-2: software / licence register.
  softwareSection: { de: 'Software & Lizenzen', en: 'Software & licences' },
  softwareEmpty: { de: 'Keine Software erfasst.', en: 'No software recorded.' },
  softwareName: { de: 'Name', en: 'Name' },
  softwareVersion: { de: 'Version', en: 'Version' },
  softwareLicence: { de: 'Lizenzreferenz', en: 'Licence ref' },
  softwareInstalledAt: { de: 'Installiert am', en: 'Installed at' },
  softwareNote: { de: 'Notiz', en: 'Note' },
  softwareAdd: { de: 'Software hinzufügen', en: 'Add software' },
  softwareRemove: { de: 'Software entfernen', en: 'Remove software' },

  // A-5: refrigerant log (HVAC classes).
  refrigerantSection: { de: 'Kältemittel-Protokoll', en: 'Refrigerant log' },
  refrigerantEmpty: { de: 'Keine Einträge.', en: 'No entries yet.' },
  refrigerantAction: { de: 'Vorgang', en: 'Action' },
  refrigerant_recover: { de: 'Rückgewinnung', en: 'Recover' },
  refrigerant_recharge: { de: 'Nachfüllung', en: 'Recharge' },
  refrigerant_reclaim: { de: 'Aufbereitung', en: 'Reclaim' },
  refrigerantQty: { de: 'Menge (kg)', en: 'Quantity (kg)' },
  refrigerantTech: { de: 'Techniker', en: 'Technician' },
  refrigerantNote: { de: 'Notiz', en: 'Note' },
  refrigerantAdd: { de: 'Eintrag hinzufügen', en: 'Add entry' },

  // G4: contracts covering this CI (warranty / lease / maintenance).
  contractsSection: { de: 'Verträge', en: 'Contracts' },
  contractsEmpty: { de: 'Keine Verträge für dieses Asset.', en: 'No contracts cover this asset.' },
  contractNumber: { de: 'Vertragsnr.', en: 'Contract no.' },
  contractVendor: { de: 'Anbieter', en: 'Vendor' },
  contractType: { de: 'Art', en: 'Type' },
  contractType_warranty: { de: 'Garantie', en: 'Warranty' },
  contractType_lease: { de: 'Leasing', en: 'Lease' },
  contractType_maintenance: { de: 'Wartung', en: 'Maintenance' },
  contractType_service: { de: 'Service', en: 'Service' },
  contractType_support: { de: 'Support', en: 'Support' },
  contractStatus: { de: 'Status', en: 'Status' },
  contractStatus_active: { de: 'Aktiv', en: 'Active' },
  contractStatus_expired: { de: 'Abgelaufen', en: 'Expired' },
  contractStatus_terminated: { de: 'Gekündigt', en: 'Terminated' },
  contractStatus_pending: { de: 'Ausstehend', en: 'Pending' },
  contractStartsOn: { de: 'Beginn', en: 'Starts' },
  contractExpiresOn: { de: 'Ende', en: 'Expires' },
  contractCost: { de: 'Kosten', en: 'Cost' },
  contractTerms: { de: 'Konditionen', en: 'Terms' },
  contractNotes: { de: 'Notizen', en: 'Notes' },
  contractPo: { de: 'Bestellnr.', en: 'PO no.' },
  contractSource_manual: { de: 'Manuell erfasst', en: 'Entered manually' },
  contractSource_goods_receipt: { de: 'Aus Wareneingang', en: 'From goods receipt' },
  contractAdd: { de: 'Vertrag hinzufügen', en: 'Add contract' },
  contractEdit: { de: 'Vertrag bearbeiten', en: 'Edit contract' },
  contractTerminate: { de: 'Vertrag kündigen', en: 'Terminate contract' },
  contractTerminateNote: { de: 'Kündigungsgrund', en: 'Reason for termination' },
  contractRemoveCoverage: { de: 'Abdeckung entfernen', en: 'Remove coverage' },
  contractCovers: { de: 'Abgedeckte Assets', en: 'Covered assets' },
  contractSave: { de: 'Speichern', en: 'Save' },
  contractCancel: { de: 'Abbrechen', en: 'Cancel' },
  contractVendorRequired: { de: 'Anbieter ist erforderlich.', en: 'Vendor is required.' },
  // ── R-235: the asset record's tabs (the prototype's six panes, plus ours) ──
  tabOverview: { de: 'Übersicht', en: 'Overview' },
  tabClassAttrs: { de: 'Klassenattribute', en: 'Class attributes' },
  tabTelemetry: { de: 'Live-Telemetrie', en: 'Live telemetry' },
  tabMovements: { de: 'Bewegungen', en: 'Movement history' },
  tabRelationships: { de: 'Beziehungen', en: 'Relationships' },
  tabFinancial: { de: 'Finanzen', en: 'Financial' },
  tabService: { de: 'Service & Nachweise', en: 'Service & records' },

  // p1 Overview
  identitySection: { de: 'Identität', en: 'Identity' },
  custodySection: { de: 'Verantwortung & Standort', en: 'Custody & placement' },
  fieldModelSku: { de: 'Modell (SKU)', en: 'Model (SKU)' },
  modelUnlinked: { de: 'nicht mit dem Katalog verknüpft', en: 'not linked to the catalogue' },
  domainsLabel: { de: 'Domänen', en: 'Domains' },
  serialNotRecorded: { de: 'nicht erfasst', en: 'not recorded' },
  registeredLabel: { de: 'Registriert', en: 'Registered' },
  registrarUnknown: { de: 'Erfasser nicht festgehalten', en: 'registrar not recorded' },
  regMethod_scan: { de: 'Etikett gescannt', en: 'label scanned' },
  regMethod_receipt: { de: 'Wareneingang', en: 'goods receipt' },
  regMethod_import: { de: 'CSV-Import', en: 'CSV import' },
  regMethod_manual: { de: 'manuell erfasst', en: 'entered by hand' },
  isStockroom: { de: 'Lagerort', en: 'stockroom' },
  noEndUser: { de: '— (kein Endnutzer)', en: '— (no end user)' },
  noEndUserExpected: {
    de: '— (diese Klasse erwartet keinen Endnutzer)',
    en: '— (this class expects no end user)',
  },
  managedByPerson: { de: 'Verantwortlich (Person)', en: 'Managed by' },
  managedByGroup: { de: 'Verantwortliche Gruppe', en: 'Managed by group' },
  departmentLabel: { de: 'Abteilung', en: 'Department' },
  lastUpdated: { de: 'Zuletzt geändert', en: 'Last updated' },
  custodyNote: {
    de: '„Zugewiesen an“ und „Verantwortlich“ sind zwei verschiedene Personen. Zugewiesen an ist, wer das Asset gerade hat — die Person, bei der man es zurückholt. Verantwortlich ist, wer technisch dafür einsteht. Ein Telefon hat einen Zugewiesenen; eine Lüftungsanlage hat einen Verantwortlichen und überhaupt keinen Endnutzer — sie steht einfach in einem Raum, und genau das hält der Standort fest. Eine Störung wird an die verantwortliche Gruppe geroutet.',
    en: '“Assigned to” and “Managed by” are two different people. Assigned to is whoever is holding the asset — the person you would ask for it back. Managed by is whoever is technically responsible for it. A phone has an assigned-to; fixed plant such as an air handler has a managed-by and no end user at all — it simply sits in a room, which is what Location records. When a fault is reported it is routed to the managed-by group.',
  },

  // p2 Class attributes
  attrsNote: {
    de: 'Einmal auf der Asset-KLASSE definiert. Für jede Einheit einer SKU gleich → Modellattribut, hier nur lesbar. Pro Einheit unterschiedlich → Asset-Attribut, wird bei der Erfassung erfragt.',
    en: 'Defined once on the asset CLASS. Same for every unit of a SKU → a model attribute, shown read-only here. Varies per unit → an asset attribute, asked for at intake.',
  },
  attrsModelLevel: {
    de: 'Vom Modell — für alle Einheiten gleich',
    en: 'From the model — same for all units',
  },
  attrsPerUnit: { de: 'Pro Einheit — unterschiedlich', en: 'Per unit — varies' },
  attrsNoModelLevel: {
    de: 'Diese Klasse hat keine Attribute auf Modellebene.',
    en: 'No model-level attributes on this class.',
  },
  attrsNoAssetLevel: {
    de: 'Diese Klasse hat keine Attribute auf Asset-Ebene.',
    en: 'No asset-level attributes on this class.',
  },
  attrsNoModelLinked: {
    de: 'Kein Katalogmodell verknüpft — die Werte stammen aus der Kopie, die bei der Erfassung angelegt wurde, nicht aus der SKU.',
    en: 'No catalogue model linked — these values come from the copy made at registration, not from the SKU.',
  },
  attrsEditPerUnit: { de: 'Werte pro Einheit bearbeiten', en: 'Edit per-unit values' },
  attrsOnDevice: { de: 'Auf dem Geräteeintrag', en: 'On the device record' },
  attrsOnDeviceNote: {
    de: 'Auf der Klasse deklariert, aber noch von nichts gelesen: diese Attribute gehören zum Geräteeintrag.',
    en: 'Declared on the class but nothing reads them yet: these belong to the device record.',
  },

  // p3 Live telemetry
  telemetryNotExpected: {
    de: 'Die Klasse {class} ist nicht als vernetztes Gerät markiert, ein Geräteeintrag ist hier also nicht zu erwarten. Wenn diese Einheiten Telemetrie senden, ändere das auf der Klasse.',
    en: 'The class {class} is not marked as a connected device, so no device record is expected here. If these units report telemetry, change that on the class.',
  },
  telemetryNoDevice: { de: 'Kein Gerät gebunden', en: 'No device bound' },
  telemetryNoDeviceMsg: {
    de: 'Ein Geräteeintrag entsteht hier nicht durch Anlegen: Controller melden sich selbst im Netz und die Bestandsaufnahme schreibt den Eintrag. Ein Mensch verbindet ihn mit einem Asset — und zwar in der Warteschlange der unregistrierten Geräte.',
    en: 'A device record is not created here: controllers announce themselves on the network and auto-inventory writes the row. A person BINDS one to an asset, and that happens in the unregistered-devices queue.',
  },
  telemetryGoToQueue: { de: 'Zu den unregistrierten Geräten', en: 'Go to unregistered devices' },
  telemetryBindingNote: {
    de: 'Ein Geräteeintrag existiert nur, weil sein Asset existiert. Jedes Gerät gehört zu genau einem Asset und kann nicht umgehängt werden. Das Asset ist immer der Master: die Bindung zu lösen lässt das Asset unberührt, umgekehrt gilt das nicht.',
    en: 'A device record exists only because its asset exists. Every device belongs to exactly one asset and cannot be moved to another. The asset is always the master — unbinding leaves the asset untouched, but not the other way round.',
  },
  telemetryBinding: { de: 'Bindung', en: 'Binding' },
  telemetryController: { de: 'Controller', en: 'Controller' },
  telemetryChannelCount: { de: 'Kanäle', en: 'Channels' },
  telemetryChannels: { de: 'Gemeldete Kanäle', en: 'Reported channels' },
  telemetryHealth: { de: 'Zustand', en: 'Health' },
  telemetryReporting: { de: 'Meldet', en: 'Reporting' },
  telemetryUnhealthy: { de: '{count} gestört', en: '{count} faulty' },
  telemetryOk: { de: 'OK', en: 'OK' },
  telemetryFaulty: { de: 'Gestört', en: 'Faulty' },
  telemetryAbsent: { de: 'Nicht vorhanden', en: 'Absent' },
  telemetryBoundSilent: {
    de: 'Gebunden, aber es kommt nichts an: dieses Gerät hat noch keinen Kanal gemeldet. Das ist nicht dasselbe wie „nicht gebunden“ — die MAC ist gesetzt, das Netz schweigt.',
    en: 'Bound but silent: this device has not announced a single channel. That is not the same as “not bound” — the MAC is set and the network is quiet.',
  },
  unbindDevice: { de: 'Bindung lösen', en: 'Unbind device' },
  unbindTitle: { de: 'Bindung zu {ref} lösen?', en: 'Unbind {ref}?' },
  unbindMsg: {
    de: 'Das Asset bleibt unverändert und behält seine ganze Historie. Das Gerät kehrt in die Warteschlange zurück und kann dort einem anderen Asset zugeordnet werden.',
    en: 'The asset is untouched and keeps its full history. The device returns to the queue, where it can be bound to a different asset.',
  },

  // p4 / p5 pane notes
  movementsNote: {
    de: 'Nur anfügen. Der Standort auf dem Datensatz ist der aktuelle Wert, dieses Journal ist die Historie. Standort zu überschreiben, ohne hier eine Zeile zu schreiben, würde die Antwort auf „wo war es im Juni, und wer hat dafür gezeichnet“ zerstören.',
    en: 'Append-only. Location on the record is the current value; this ledger is the history. Overwriting placement without a row here would destroy the answer to “where was it in June, and who signed for it”.',
  },
  relationsNote: {
    de: 'Wie dieses Asset mit anderen im Register zusammenhängt. Diese Verknüpfungen sind es, die „was versorgt das“ und „was hat was ersetzt“ beantwortbar machen, wenn eine Störung gemeldet oder eine Einheit getauscht wird.',
    en: 'How this asset connects to others in the register. These links are what let you answer “what does this serve” and “what replaced what” when a fault is reported or a unit is swapped out.',
  },

  // p6 Financial
  financialNote: {
    de: 'Lieferant, Kosten und Belegreferenzen sind optional. Für ein Asset, das schon im Bestand war, trägt man sie von Hand ein; für alles über die Beschaffung Gekaufte füllt der Wareneingang sie automatisch.',
    en: 'Supplier, cost and paperwork references are optional. Enter them by hand for an asset you already owned; for anything bought through Procurement they are filled in automatically at goods receipt.',
  },
  purchaseSection: { de: 'Einkauf', en: 'Purchase' },
  ownershipSection: { de: 'Eigentum & Nutzungsdauer', en: 'Ownership & life' },
  receiptRef: { de: 'Wareneingangsbeleg', en: 'Receipt ref' },
  receiptManual: { de: '— (von Hand erfasst, kein Wareneingang)', en: '— (entered by hand, no receipt)' },
  skuUnitCost: { de: 'Stückkosten der SKU', en: 'Unit cost of SKU' },

  // p7 Service & records — ours, not the prototype's
  serviceNote: {
    de: 'Diese Registerkarte ist unsere, nicht die des Prototyps. Wartungsstrategie, Softwarebestand, Kältemittelprotokoll und Ereignisverlauf sind Fakten, die dieser Standort führt; sie in eine der sechs Registerkarten des Prototyps zu falten würde beide unleserlich machen.',
    en: "This tab is ours, not the prototype's. Maintenance strategy, the software register, the refrigerant log and the event history are facts this site keeps; folding them into one of the prototype's six panes would make both unreadable.",
  },
  serviceSection: { de: 'Wartung', en: 'Maintenance' },

  // ── R-235: the prototype's Assign dialog (custody, not a move) ──
  assignAsset: { de: 'Zuweisen', en: 'Assign' },
  assignTitle: { de: 'Zuweisen — {tag}', en: 'Assign — {tag}' },
  assignedToEndUser: { de: 'Zugewiesen an (Endnutzer)', en: 'Assigned to (end user)' },
  managedByResponsible: { de: 'Verantwortlich (Person)', en: 'Managed by (responsible)' },
  assignHolderPh: { de: 'Person, die das Asset hat', en: 'Person holding the asset' },
  assignResponsiblePh: { de: 'Technisch verantwortliche Person', en: 'Technically responsible person' },
  assignExpectsEndUser: {
    de: 'Diese Klasse erwartet einen Endnutzer.',
    en: 'This class expects an end user.',
  },
  assignNoEndUserHint: {
    de: 'Für ortsfeste Technik leer lassen — dort zählt „Verantwortlich“.',
    en: 'Leave empty for fixed plant — use Managed by instead.',
  },
  assignExpectsManagedBy: {
    de: 'Diese Klasse erwartet eine verantwortliche Person.',
    en: 'This class expects a responsible person.',
  },
  assignManagedByHint: {
    de: 'Störungen werden an die verantwortliche Gruppe geroutet; das hier ist, wen man anruft.',
    en: 'Faults route to the owning group; this is who you call.',
  },
  assignCostCenterPh: { de: 'z. B. DUBAI-TEST / Klima', en: 'e.g. DUBAI-TEST / Climate' },
  assignDepartmentPh: { de: 'z. B. Farmbetrieb', en: 'e.g. Farm Operations' },
  assignWillLog: {
    de: 'Der Halter ändert sich — das wird als Bewegung („Neu zugewiesen“) protokolliert.',
    en: 'The holder changes — this will be recorded as a movement (“Re-assigned”).',
  },
  assignWillNotLog: {
    de: 'Der Halter bleibt gleich — es wird keine Bewegung protokolliert. Eine Korrektur der Kostenstelle ist keine Übergabe.',
    en: 'The holder is unchanged — no movement is recorded. A cost-centre correction is not a handover.',
  },
  assignSave: { de: 'Verantwortung speichern', en: 'Save custody' },
  // The floor tool says it the prototype's way — on a phone, "Assign" alone
  // reads as "assign a task".
  wizLoadError: {
    de: 'Voraussetzungen konnten nicht geladen werden',
    en: 'Could not load the prerequisites',
  },
  wizSerialChecking: { de: 'Prüfe auf Duplikate…', en: 'Checking for duplicates…' },
  wizSerialCheckFailed: {
    de: 'Die Duplikatsprüfung ist fehlgeschlagen — weiter geht es erst, wenn sie durchläuft. Ein ungeprüfter Fortschritt kann eine zweite Registerzeile für dieselbe Einheit erzeugen.',
    en: 'The duplicate check failed — you cannot continue until it runs. Advancing unchecked is how one physical unit ends up with two register rows.',
  },
  wizFromDevice: {
    de: 'Gestartet aus der Geräte-Warteschlange: {ref} wird nach der Registrierung automatisch mit dem neuen Asset verbunden.',
    en: 'Started from the device queue: {ref} will be bound to the new asset once it is registered.',
  },
  classTotal: { de: '{total} Klassen', en: '{total} classes' },
  expandAll: { de: 'Alle aufklappen', en: 'Expand all' },
  collapseAll: { de: 'Alle zuklappen', en: 'Collapse all' },
  colStandardRef: { de: 'Norm-Referenz', en: 'Standard' },
  assignToPerson: { de: 'Person zuweisen', en: 'Assign to person' },
  assignNobody: { de: '— niemand', en: '— nobody' },
  // Asset record "A · Ledger" layout (all-assets-enhancement).
  moreActions: { de: 'Mehr', en: 'More' },
  lifecycleLabel: { de: 'Lebenszyklus', en: 'Lifecycle' },
  lifecycleMissing: {
    de: 'Als vermisst gemeldet. Der zuletzt erfasste Ort steht unter „Verantwortung & Standort“; „Bewegen / Umbuchen“ erfasst den Fund.',
    en: 'Marked missing. The last recorded place is under Custody; Move / Transfer records it once it is found.',
  },
  recentActivity: { de: 'Letzte Bewegungen', en: 'Recent activity' },
  allMovements: { de: 'Alle Bewegungen →', en: 'All movements →' },
  railDevice: { de: 'Gerät', en: 'Device' },
  railComingUp: { de: 'Demnächst', en: 'Coming up' },
  openTelemetry: { de: 'Telemetrie →', en: 'Telemetry →' },
  dueInDays: { de: 'in {n} T', en: 'in {n} d' },
  dueToday: { de: 'heute fällig', en: 'due today' },
  dueOverdue: { de: '{n} T überfällig', en: '{n} d overdue' },
  docsMissingRail: { de: '{kinds} fehlt →', en: '{kinds} missing →' },
  docsOnFile: { de: 'Vollständig', en: 'All on file' },
  colChannel: { de: 'Kanal', en: 'Channel' },
  colKind: { de: 'Art', en: 'Kind' },
  relationsEmpty: { de: 'Noch keine verbundenen Assets.', en: 'No related assets yet.' },
  // Input placeholders on the asset record's forms — examples, never values.
  phName: {
    de: 'z. B. Munters MLT800 Adsorptionstrockner',
    en: 'e.g. Munters MLT800 desiccant dehumidifier',
  },
  phDescription: { de: 'Wozu es dient und wo es eingesetzt wird', en: 'What it does and where it is used' },
  phManufacturer: { de: 'z. B. Munters', en: 'e.g. Munters' },
  phModel: { de: 'z. B. MLT800', en: 'e.g. MLT800' },
  phSerial: { de: 'Wie auf dem Typenschild', en: 'As printed on the rating plate' },
  phYear: { de: 'z. B. 2024', en: 'e.g. 2024' },
  phSupplier: { de: 'Lieferant suchen oder eingeben', en: 'Search or type a supplier' },
  phPoRef: { de: 'z. B. PO-2026-0311', en: 'e.g. PO-2026-0311' },
  phCost: { de: 'z. B. 22000.00', en: 'e.g. 22000.00' },
  phCostCenter: { de: 'z. B. CC-FARM-110', en: 'e.g. CC-FARM-110' },
  phDepreciation: { de: 'z. B. Linear', en: 'e.g. Straight line' },
  phUsefulLife: { de: 'z. B. 7', en: 'e.g. 7' },
  phIfrs: { de: 'z. B. Technische Anlagen', en: 'e.g. Plant & machinery' },
  phMaintStrategy: { de: 'z. B. Vorbeugend · quartalsweise', en: 'e.g. Preventive · quarterly' },
  phServiceContract: { de: 'z. B. Anbieter und Vertragsnummer', en: 'e.g. vendor and contract number' },
  phNotes: { de: 'Was die nächste Person wissen sollte', en: 'Anything the next person should know' },
  phGroup: { de: '— Gruppe wählen —', en: '— select group —' },
  phAttr: { de: '{label} eingeben', en: 'Enter {label}' },
  phAttrSelect: { de: '— wählen —', en: '— select —' },
  phRefQty: { de: 'z. B. 3,2', en: 'e.g. 3.2' },
  phRefTech: { de: 'Name oder Zertifikatsnr. der Fachkraft', en: 'Technician name or certificate no.' },
  phRefNote: { de: 'z. B. Dichtheitsprüfung bestanden', en: 'e.g. Leak test passed' },
  phSwName: { de: 'z. B. Windows 11 Pro', en: 'e.g. Windows 11 Pro' },
  phSwVersion: { de: 'z. B. 23H2', en: 'e.g. 23H2' },
  phSwLicence: { de: 'Lizenzschlüssel oder Vertragsnr.', en: 'Licence key or contract no.' },
  phSwNote: { de: 'Optionale Notiz', en: 'Optional note' },
  phContractVendor: { de: 'z. B. Wartungsfirma', en: 'e.g. service company' },
  phContractTerms: {
    de: 'z. B. Quartalsbesuche, 48 h Reaktionszeit',
    en: 'e.g. quarterly visits, 48 h response',
  },
  phRetireReason: { de: 'z. B. Reparatur unwirtschaftlich', en: 'e.g. beyond economic repair' },
  phDisposalVendor: { de: 'z. B. zertifizierter Recycler', en: 'e.g. certified recycler' },

  // ─── QR Labels, canvas design "A · Ledger drill" ───────────────────────────
  lpLoadError: { de: 'Label-Pool konnte nicht geladen werden', en: 'Could not load the label pool' },
  lpStatBound: { de: 'Mit Asset verknüpft', en: 'Bound to an asset' },
  lpStatBatches: { de: 'Gedruckte Stapel', en: 'Batches printed' },
  lpBlockedKey: { de: 'Wareneingang blockiert', en: 'Intake blocked' },
  lpBlockedMsg: {
    de: 'Diese Klassen haben keine freien Labels — eine Einheit davon kann erst nach einem neuen Stapel angenommen werden.',
    en: 'These classes have no unused labels — a unit of them cannot be received until a batch is printed.',
  },
  lpGenerateFor: { de: '{name} · erzeugen →', en: '{name} · generate →' },
  lpTabAll: { de: 'Alle Klassen ({n})', en: 'All classes ({n})' },
  lpTabNone: { de: 'Keine übrig ({n})', en: 'None left ({n})' },
  lpTabSome: { de: 'Mit freien ({n})', en: 'Has unused ({n})' },
  lpTabStateAll: { de: 'Alle ({n})', en: 'All ({n})' },
  lpTabStateUnused: { de: 'Frei ({n})', en: 'Unused ({n})' },
  lpTabStateBound: { de: 'Verknüpft ({n})', en: 'Bound ({n})' },
  lpTabStateVoid: { de: 'Verworfen ({n})', en: 'Void ({n})' },
  lpColPrefix: { de: 'Präfix', en: 'Prefix' },
  lpColPool: { de: 'Pool', en: 'Pool' },
  lpColUnused: { de: 'Frei', en: 'Unused' },
  lpColLastBatch: { de: 'Letzter Stapel', en: 'Last batch' },
  lpColUse: { de: 'Nutzung', en: 'Use' },
  lpColGenerated: { de: 'Erzeugt', en: 'Generated' },
  lpBarText: {
    de: '{bound} verknüpft · {unused} frei · {void} verworfen',
    en: '{bound} bound · {unused} unused · {void} void',
  },
  lpBarAria: {
    de: '{bound} verknüpft, {unused} frei, {void} verworfen',
    en: '{bound} bound, {unused} unused, {void} void',
  },
  lpNoneLeft: { de: 'Keine übrig', en: 'None left' },
  lpLastBatchSub: { de: '{count} Labels · {size}', en: '{count} labels · {size}' },
  lpNoBatchYet: { de: 'noch kein Stapel', en: 'no batch yet' },
  lpSizeNotRecorded: { de: 'nicht erfasst', en: 'not recorded' },
  lpSizeNotRecordedTip: {
    de: 'Dieses Blatt wurde gedruckt, bevor die Größe erfasst wurde',
    en: 'This sheet was printed before the size was recorded',
  },
  lpOpenBatches: { de: 'Stapel öffnen', en: 'Open batches' },
  lpOpenSheet: { de: 'Blatt öffnen', en: 'Open sheet' },
  lpPrint: { de: 'Drucken', en: 'Print' },
  lpGenerate: { de: 'Erzeugen', en: 'Generate' },
  lpPrefixIsCode: { de: 'Klassencode', en: 'class code' },
  lpPrefixChip: { de: 'Präfix {p}', en: 'Prefix {p}' },
  lpClassDesc: {
    de: 'Jedes für diese Klasse gedruckte Blatt, neueste zuerst. Öffnen zeigt die Labels; erneut drucken ändert nie ein Tag.',
    en: 'Every sheet printed for this class, newest first. Open one to see its labels, or print it again — reprinting never changes a tag.',
  },
  lpBatchMeta: { de: '{count} Labels · erzeugt {when}', en: '{count} labels · generated {when}' },
  lpLegendUnused: {
    de: 'Frei — am Wareneingang abziehen und scannen',
    en: 'Unused — peel and scan at intake',
  },
  lpLegendBound: { de: 'Verknüpft — öffnet sein Asset', en: 'Bound — opens its asset' },
  lpLegendVoid: { de: 'Verworfen', en: 'Void' },
  lpAssetLink: { de: 'Asset →', en: 'Asset →' },
  lpBoundOn: { de: 'Verknüpft {when}', en: 'Bound {when}' },
  lpVoidTitle: { de: '{tag} verwerfen?', en: 'Void {tag}?' },
  lpVoidMsg: {
    de: 'Für ein beschädigtes, verlorenes oder fehlgedrucktes Etikett. Es kann danach nie mehr mit einem Asset verknüpft werden, und die Nummer wird nicht neu vergeben — die Lücke bleibt als Nachweis in der Sequenz.',
    en: 'Use this for a sticker that is damaged, lost or misprinted. It can never be bound to an asset afterwards, and the number is not reissued — the gap stays in the sequence as a record.',
  },
  lpVoidConfirm: { de: 'Label verwerfen', en: 'Void label' },
  lpToastVoided: { de: '{tag} verworfen', en: '{tag} voided' },
  lpToastGenerated: { de: 'Stapel erzeugt — {first} → {last}', en: 'Batch generated — {first} → {last}' },
  lpSearchPh: { de: 'Klasse, Code oder Tag suchen', en: 'Search class, code or tag' },
  lpSearchEmpty: { de: 'Nichts passt zu „{q}“.', en: 'Nothing matches “{q}”.' },
  lpBatchTitle: { de: 'Stapel {range}', en: 'Batch {range}' },
  lpBatchMetaPrefix: {
    de: '{prefix} · {count} Labels · erzeugt {when}',
    en: '{prefix} · {count} labels · generated {when}',
  },
  lpPrintDescPrefix: {
    de: '{prefix} — freie Labels sind vorausgewählt. Der QR-Code enthält nur das Tag.',
    en: '{prefix} — unused labels are selected. The QR carries the bare tag, which is what intake scans.',
  },
  lpClassPh: { de: 'Klasse wählen', en: 'Choose a class' },
  lpSizePh: { de: 'Etikettengröße wählen', en: 'Choose a sticker size' },
  lpCountPh: { de: 'z. B. 40', en: 'e.g. 40' },
  lpCountRange: { de: 'Eine Zahl von 1 bis 500 eingeben.', en: 'Enter a number from 1 to 500.' },
  lpPreviewKey: { de: 'Diese Tags werden erzeugt', en: 'These tags will be minted' },
  lpPreviewWarn: {
    de: 'Nummern werden nie wiederverwendet. {count} Labels verbrauchen diese Tags dauerhaft, auch wenn das Blatt nie gedruckt wird.',
    en: 'Numbers are never reused. Generating {count} uses up these tags permanently, even if the sheet is never printed.',
  },
  lpGenerateN: { de: '{count} Labels erzeugen', en: 'Generate {count} labels' },
  lpEmptyState: { de: 'Keine Klasse in dieser Ansicht', en: 'No class in this view' },
  lpEmptyStateMsg: { de: 'Einen anderen Filter wählen.', en: 'Choose another filter.' },
  lpBatchNotFound: { de: 'Stapel nicht gefunden', en: 'Batch not found' },
  lpBatchNotFoundMsg: {
    de: 'Dieser Link zeigt auf einen Stapel, den es in dieser Klasse nicht gibt.',
    en: 'This link points to a batch that does not exist in this class.',
  },
  // Print sheet
  lpPrintTitle: { de: 'Drucken · {range}', en: 'Print · {range}' },
  lpPrintDesc: {
    de: 'Freie Labels sind vorausgewählt. Der QR-Code enthält nur das Tag — das, was der Wareneingang scannt.',
    en: 'Unused labels are selected. The QR carries the bare tag — what intake scans.',
  },
  lpPrintSelection: { de: 'Auswahl', en: 'Selection' },
  lpPrintUnusedOnly: { de: 'Nur freie auswählen', en: 'Select unused only' },
  lpPrintOnSheet: { de: 'Auf diesem Blatt', en: 'On this sheet' },
  lpPrintToPrint: { de: 'Zum Drucken', en: 'To print' },
  lpPrintSticker: { de: 'Etikett', en: 'Sticker' },
  lpPrintHint: {
    de: 'Ein Label anklicken, um es aus- oder abzuwählen. Verworfene Labels werden nie gedruckt.',
    en: 'Click a label to include or leave it out. Void labels are never printed.',
  },
  lpPrintEmpty: { de: 'Nichts zu drucken', en: 'Nothing to print' },
  lpPrintEmptyMsg: {
    de: 'Jedes Label dieses Stapels ist verworfen.',
    en: 'Every label in this batch is void.',
  },
  lpPrintNoBatch: { de: 'Kein Stapel gewählt', en: 'No batch chosen' },
  lpPrintNoBatchMsg: {
    de: 'Diese Seite braucht einen Stapel oder Asset-Tags in der Adresse.',
    en: 'This page needs a batch or asset tags in its address.',
  },
} satisfies Namespace;
